/**
 * Elite Batch 19 Part 1 — Workflow Templates 1748–1767
 * Companies with outdated tech, companies with partner programs, companies with target personas on site,
 * company top customers from URL, competitor ad-spend trigger, competitor battlecard generator,
 * competitor comparison page accounts, competitor displacement alert system, competitor top customers from URL,
 * conference roadshow follow-up, conference speaker pitch, contact-to-account matching,
 * contract renewal outreach, creator sponsorship outreach, custom demo brief generator,
 * custom video brief for outreach, customer advocate recruiting, customer data proof point,
 * customer health expansion signal, customer reference match.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 19 PART 1 — TEMPLATES 1748–1767
// =============================================================================

export const ELITE_BATCH19_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1748. COMPANIES WITH OUTDATED TECH (8 nodes)
  // ===================================================================

  "Companies with Outdated Tech": {
    summary:
      "Runs on a schedule to identify companies using outdated technology stacks. Queries BuiltWith for technology profiles, scores technology risk with runCode, uses AI to map replacement solutions, discovers decision-maker contacts via API, validates emails, drafts a personalized migration pitch with AI, and creates a CRM deal plus sends the outreach email via HubSpot.",
    nodes: [
      trigger("Schedule: Weekly outdated tech scan", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Fires weekly to scan target accounts for outdated or end-of-life technology stacks using BuiltWith, initiating a migration-focused outbound sequence",
        setup:
          "Configure BuiltWith API key. Define target domain list in the schedule payload or CRM list. Set up Hunter.io API for contact discovery. Provide NeverBounce API key for email validation. Configure OpenAI key for AI pitch generation. Ensure HubSpot token has deal and contact creation access.",
        inputFields: [
          { name: "target_domain", type: "string", example: "acme-corp.com" },
          { name: "target_company", type: "string", example: "Acme Corp" },
        ],
      }),

      // 2. BuiltWith tech lookup API
      n(
        "2",
        "hitApi",
        "BuiltWith: Lookup technology profile for target domain",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.builtwith.com/free1/api.json?KEY={builtwith_api_key}&LOOKUP={target_domain}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "Results[0].Result.Paths[0].Technologies",
                customName: "tech_list_raw",
                type: "string",
              },
              {
                name: "Results[0].Result.Paths[0].Domain",
                customName: "scanned_domain",
                type: "string",
              },
              {
                name: "Results[0].Result.Paths[0].Url",
                customName: "scanned_url",
                type: "string",
              },
              {
                name: "Results[0].Result.IsDB",
                customName: "is_in_database",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Technology risk scoring runCode
      n("3", "runCode", "Score technology stack for outdated/EOL risk", 520, {
        runCodeDetails: {
          code: `const techs = JSON.parse(variables.tech_list_raw || '[]');
const eolTech = ['jQuery 1.x','Angular 1','PHP 5','Python 2','Flash','Silverlight','IE-specific','Windows Server 2008','MySQL 5.5','Apache 2.2','OpenSSL 1.0','TLS 1.0','Bootstrap 2','Backbone.js','Ember 1','Rails 3','Django 1','Node.js 8','Java 7','ColdFusion'];
const outdated = [];
const current = [];
for (const t of techs) {
  const name = t.Name || t.name || t.Tag || '';
  const matched = eolTech.find(e => name.toLowerCase().includes(e.toLowerCase()));
  if (matched) { outdated.push(name); } else { current.push(name); }
}
output.outdated_technologies = outdated.join(', ') || 'None detected';
output.current_technologies = current.slice(0, 10).join(', ');
output.outdated_count = outdated.length;
output.total_tech_count = techs.length;
output.risk_score = Math.min(100, outdated.length * 20 + (outdated.length > 3 ? 20 : 0));
output.risk_level = output.risk_score >= 60 ? 'high' : output.risk_score >= 30 ? 'medium' : 'low';
output.migration_urgency = outdated.length >= 3 ? 'critical' : outdated.length >= 1 ? 'recommended' : 'optional';`,
          outputVariables: [
            { name: "outdated_technologies", type: "string" },
            { name: "current_technologies", type: "string" },
            { name: "outdated_count", type: "number" },
            { name: "total_tech_count", type: "number" },
            { name: "risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "migration_urgency", type: "string" },
          ],
        },
      }),

      // 4. AI replacement mapper
      aiNode(
        "4",
        "AI map modern replacement solutions for outdated tech",
        760,
        "You are a technology migration consultant. Given a list of outdated technologies and current stack, recommend specific modern replacement technologies, estimate migration complexity, and craft a business case. Return JSON: {replacement_map, migration_complexity, estimated_savings, business_case_summary, top_risk, recommended_timeline}",
        "Company: {target_company} ({target_domain}) | Outdated: {outdated_technologies} | Current stack: {current_technologies} | Risk score: {risk_score}/100 | Urgency: {migration_urgency}",
        [
          { field: "replacement_map", as: "replacement_map", type: "string" },
          {
            field: "migration_complexity",
            as: "migration_complexity",
            type: "string",
          },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
          {
            field: "business_case_summary",
            as: "business_case_summary",
            type: "string",
          },
          { field: "top_risk", as: "top_risk", type: "string" },
          {
            field: "recommended_timeline",
            as: "recommended_timeline",
            type: "string",
          },
        ],
      ),

      // 5. Contact discovery API (Hunter.io)
      n("5", "hitApi", "Hunter.io: Discover decision-maker contacts", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={target_domain}&api_key={hunter_api_key}&department=it,engineering&limit=3",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "contact_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "contact_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "data.organization",
              customName: "org_name",
              type: "string",
            },
          ],
        },
      }),

      // 6. Email validation API (NeverBounce)
      n("6", "hitApi", "NeverBounce: Validate discovered email address", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "result", customName: "email_validity", type: "string" },
            { name: "flags", customName: "email_flags", type: "string" },
          ],
        },
      }),

      // 7. AI migration pitch draft
      aiNode(
        "7",
        "AI draft personalized migration pitch email",
        1480,
        "You are a B2B sales copywriter specializing in technology modernization. Write a concise, personalized outreach email to a technology leader about their outdated tech stack. Reference specific technologies found on their site, explain the business risk, and position your solution as the migration partner. Be consultative, not salesy. Return JSON: {subject_line, email_body, ps_line, cta_text}",
        "Contact: {contact_first_name} {contact_last_name}, {contact_title} at {target_company} | Outdated tech: {outdated_technologies} | Risk: {risk_level} ({risk_score}/100) | Business case: {business_case_summary} | Top risk: {top_risk} | Replacements: {replacement_map} | Savings: {estimated_savings} | Timeline: {recommended_timeline}",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "email_body", as: "pitch_body", type: "string" },
          { field: "ps_line", as: "pitch_ps", type: "string" },
          { field: "cta_text", as: "pitch_cta", type: "string" },
        ],
      ),

      // 8. CRM deal creation + email send HubSpot
      hubspotPostNode(
        "8",
        "Create HubSpot deal and send migration pitch email",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Tech Migration — {target_company}","dealstage":"appointmentscheduled","pipeline":"default","amount":"{estimated_savings}","description":"Outdated tech: {outdated_technologies}. Risk: {risk_score}/100. Complexity: {migration_complexity}. Timeline: {recommended_timeline}. Contact: {contact_first_name} {contact_last_name} ({contact_email}).","hubspot_owner_id":"{owner_id}"}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "created_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1749. COMPANIES WITH PARTNER PROGRAMS (8 nodes)
  // ===================================================================

  "Companies with Partner Programs": {
    summary:
      "Runs on a schedule to find companies with active partner or affiliate programs. Scrapes partner program pages via API, uses AI to assess partnership fit, extracts program terms with runCode, discovers decision-makers via API, validates emails, drafts a co-marketing partnership proposal with AI, and creates a CRM partnership entry plus sends a Slack notification.",
    nodes: [
      trigger("Schedule: Weekly partner program scout", "schedule", {
        schedule: "Every Wednesday at 9:00 AM",
        howItStarts:
          "Fires weekly to discover and evaluate companies with partner/affiliate programs, identifying co-marketing and channel partnership opportunities",
        setup:
          "Configure ScrapingBee API key for partner page scraping. Set up Hunter.io API for contact discovery. Provide NeverBounce key for validation. Configure OpenAI for AI analysis. Ensure HubSpot token has deal creation access. Set up Slack webhook for partnerships channel.",
        inputFields: [
          {
            name: "target_domain",
            type: "string",
            example: "saas-company.com",
          },
          {
            name: "target_company",
            type: "string",
            example: "SaaS Company Inc",
          },
          {
            name: "our_product",
            type: "string",
            example: "OmniEngage marketing platform",
          },
        ],
      }),

      // 2. Partner page scrape API
      n(
        "2",
        "hitApi",
        "ScrapingBee: Scrape partner/affiliate program page",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url=https://{target_domain}/partners&render_js=false&extract_rules=%7B%22page_text%22:%7B%22selector%22:%22body%22,%22type%22:%22text%22%7D,%22links%22:%7B%22selector%22:%22a%22,%22type%22:%22list%22,%22output%22:%22%40href%22%7D%7D",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "page_text",
                customName: "partner_page_text",
                type: "string",
              },
              {
                name: "links",
                customName: "partner_page_links",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. AI fit analysis
      aiNode(
        "3",
        "AI assess partnership program fit and opportunity",
        520,
        "You are a strategic partnerships analyst. Given the text of a company's partner program page, analyze the partnership opportunity. Determine the type of program (technology, referral, affiliate, reseller, co-sell), assess fit with our product, identify key benefits, and estimate partnership revenue potential. Return JSON: {program_type, fit_score, fit_reasoning, key_benefits, revenue_potential, partnership_tier, complementary_features, target_audience_overlap}",
        "Our product: {our_product} | Target: {target_company} ({target_domain}) | Partner page content: {partner_page_text}",
        [
          { field: "program_type", as: "program_type", type: "string" },
          { field: "fit_score", as: "fit_score", type: "number" },
          { field: "fit_reasoning", as: "fit_reasoning", type: "string" },
          { field: "key_benefits", as: "key_benefits", type: "string" },
          {
            field: "revenue_potential",
            as: "revenue_potential",
            type: "string",
          },
          { field: "partnership_tier", as: "partnership_tier", type: "string" },
          {
            field: "complementary_features",
            as: "complementary_features",
            type: "string",
          },
          {
            field: "target_audience_overlap",
            as: "target_audience_overlap",
            type: "string",
          },
        ],
      ),

      // 4. Terms extraction runCode
      n("4", "runCode", "Extract partner program terms and requirements", 760, {
        runCodeDetails: {
          code: `const text = (variables.partner_page_text || '').toLowerCase();
const commissionMatch = text.match(/(\\d+)\\s*%\\s*(commission|rev\\s*share|revenue)/);
output.commission_rate = commissionMatch ? commissionMatch[1] + '%' : 'Not specified';
const tierMatch = text.match(/(gold|silver|bronze|platinum|premier|elite|certified|preferred)/i);
output.available_tiers = tierMatch ? tierMatch[0] : 'Standard';
output.has_mdf = text.includes('market development') || text.includes('mdf') ? 'yes' : 'no';
output.has_co_marketing = text.includes('co-marketing') || text.includes('joint marketing') ? 'yes' : 'no';
output.has_referral = text.includes('referral') ? 'yes' : 'no';
output.has_reseller = text.includes('resell') || text.includes('reseller') ? 'yes' : 'no';
output.application_url = (variables.partner_page_links || '').split(',').find(l => l.includes('apply') || l.includes('register') || l.includes('join')) || '';
output.program_summary = 'Type: ' + variables.program_type + ' | Commission: ' + output.commission_rate + ' | Tiers: ' + output.available_tiers + ' | MDF: ' + output.has_mdf + ' | Co-marketing: ' + output.has_co_marketing;`,
          outputVariables: [
            { name: "commission_rate", type: "string" },
            { name: "available_tiers", type: "string" },
            { name: "has_mdf", type: "string" },
            { name: "has_co_marketing", type: "string" },
            { name: "has_referral", type: "string" },
            { name: "has_reseller", type: "string" },
            { name: "application_url", type: "string" },
            { name: "program_summary", type: "string" },
          ],
        },
      }),

      // 5. Decision-maker lookup API
      n(
        "5",
        "hitApi",
        "Hunter.io: Find partnerships/BD decision-makers",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={target_domain}&api_key={hunter_api_key}&department=executive,business_development&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "partner_contact_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "partner_contact_first",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "partner_contact_last",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "partner_contact_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Email validation API
      n("6", "hitApi", "NeverBounce: Validate partner contact email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={partner_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "result",
              customName: "partner_email_validity",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI co-marketing proposal draft
      aiNode(
        "7",
        "AI draft co-marketing partnership proposal email",
        1480,
        "You are a strategic partnerships outreach specialist. Write a professional, value-driven email proposing a partnership. Reference the specific partner program details, explain mutual benefits, highlight complementary features, and propose a concrete next step. Return JSON: {subject_line, email_body, mutual_benefit_hook, proposed_next_step}",
        "Contact: {partner_contact_first} {partner_contact_last}, {partner_contact_title} at {target_company} | Our product: {our_product} | Program type: {program_type} | Fit score: {fit_score}/100 | Key benefits: {key_benefits} | Revenue potential: {revenue_potential} | Complementary features: {complementary_features} | Audience overlap: {target_audience_overlap} | Commission: {commission_rate} | Co-marketing available: {has_co_marketing}",
        [
          { field: "subject_line", as: "proposal_subject", type: "string" },
          { field: "email_body", as: "proposal_body", type: "string" },
          {
            field: "mutual_benefit_hook",
            as: "mutual_benefit_hook",
            type: "string",
          },
          {
            field: "proposed_next_step",
            as: "proposed_next_step",
            type: "string",
          },
        ],
      ),

      // 8. CRM partnership entry + Slack notify
      hubspotPostNode(
        "8",
        "Create HubSpot partnership deal and notify team",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Partnership — {target_company} ({program_type})","dealstage":"qualifiedtobuy","pipeline":"default","description":"Fit score: {fit_score}/100. Program: {program_type}. Commission: {commission_rate}. Revenue potential: {revenue_potential}. Complementary: {complementary_features}. Contact: {partner_contact_first} {partner_contact_last} ({partner_contact_email}). Next step: {proposed_next_step}."}',
          },
        ],
        [
          { name: "id", customName: "partnership_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "partnership_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1750. COMPANIES WITH TARGET PERSONAS ON SITE (9 nodes)
  // ===================================================================

  "Companies with Target Personas on Site": {
    summary:
      "Receives a webhook when a visitor matching a target persona is identified on the website. Filters the visitor through persona criteria with runCode, enriches the company via Clearbit, scores against ICP with runCode, gates on ICP fit via condition, discovers additional contacts via API, validates emails with a waterfall API, uses AI to draft a behaviorally-personalized email, and upserts the contact to CRM plus sends an alert email via HubSpot.",
    nodes: [
      trigger("Webhook: Target persona identified on site", "webhook_app", {
        source: "clearbit_reveal",
        event: "visitor.identified",
        howItStarts:
          "Fires when a website visitor is identified as matching a target persona profile through Clearbit Reveal or similar visitor identification, sending the raw visitor payload for processing",
        setup:
          "Configure Clearbit Reveal webhook for visitor identification events. Set up Clearbit Enrichment API for company data. Provide Hunter.io key for contact discovery. Add NeverBounce key for validation. Configure OpenAI for AI drafting. Ensure HubSpot token has contact upsert access.",
        inputFields: [],
      }),

      // 2. Persona filter runCode
      n(
        "2",
        "runCode",
        "Filter and extract persona attributes from visitor data",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const visitor = payload.data || payload;
const company = visitor.company || visitor.organization || {};
const person = visitor.person || visitor.visitor || {};
output.visitor_ip = visitor.ip || visitor.visitor_ip || '';
output.visitor_domain = company.domain || visitor.domain || '';
output.visitor_company = company.name || visitor.company_name || '';
output.visitor_industry = company.industry || company.category?.industry || '';
output.visitor_employee_count = parseInt(company.metrics?.employees || company.employee_count || '0');
output.visitor_pages = JSON.stringify(visitor.pages_viewed || visitor.page_urls || []);
output.visitor_session_duration = parseInt(visitor.session_duration || visitor.time_on_site || '0');
output.persona_match = person.role || visitor.matched_persona || '';
output.visitor_country = company.geo?.country || visitor.country || '';
const targetPersonas = ['engineering','product','marketing','sales','cto','vp','director'];
const matchedPersona = targetPersonas.find(p => (output.persona_match || '').toLowerCase().includes(p));
output.is_target_persona = matchedPersona ? 'yes' : 'no';
output.matched_persona_type = matchedPersona || 'unknown';`,
            outputVariables: [
              { name: "visitor_ip", type: "string" },
              { name: "visitor_domain", type: "string" },
              { name: "visitor_company", type: "string" },
              { name: "visitor_industry", type: "string" },
              { name: "visitor_employee_count", type: "number" },
              { name: "visitor_pages", type: "string" },
              { name: "visitor_session_duration", type: "number" },
              { name: "persona_match", type: "string" },
              { name: "visitor_country", type: "string" },
              { name: "is_target_persona", type: "string" },
              { name: "matched_persona_type", type: "string" },
            ],
          },
        },
      ),

      // 3. Company enrichment Clearbit API
      n("3", "hitApi", "Clearbit: Enrich visitor company data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "enriched_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            { name: "tech", customName: "enriched_tech_stack", type: "string" },
            {
              name: "category.subIndustry",
              customName: "enriched_sub_industry",
              type: "string",
            },
            { name: "geo.city", customName: "enriched_city", type: "string" },
            {
              name: "description",
              customName: "enriched_description",
              type: "string",
            },
          ],
        },
      }),

      // 4. ICP scoring runCode
      n("4", "runCode", "Score company against ideal customer profile", 760, {
        runCodeDetails: {
          code: `let score = 0;
const employees = parseInt(variables.enriched_employees || '0');
if (employees >= 50 && employees <= 5000) score += 25;
else if (employees > 5000) score += 15;
const revenue = variables.enriched_revenue || '';
if (revenue.includes('$10M') || revenue.includes('$50M') || revenue.includes('$100M')) score += 25;
const targetIndustries = ['technology','software','saas','fintech','healthtech','ecommerce'];
if (targetIndustries.some(i => (variables.enriched_industry || '').toLowerCase().includes(i))) score += 20;
const sessionDuration = parseInt(variables.visitor_session_duration || '0');
if (sessionDuration > 120) score += 15;
else if (sessionDuration > 60) score += 10;
const pages = JSON.parse(variables.visitor_pages || '[]');
const highIntentPages = pages.filter(p => p.includes('pricing') || p.includes('demo') || p.includes('features'));
if (highIntentPages.length > 0) score += 15;
output.icp_score = Math.min(100, score);
output.icp_tier = score >= 70 ? 'tier_1' : score >= 40 ? 'tier_2' : 'tier_3';
output.is_icp_fit = score >= 50 ? 'yes' : 'no';
output.high_intent_pages = highIntentPages.join(', ') || 'none';
output.scoring_breakdown = 'Size:' + (employees >= 50 ? '+' : '-') + ' Revenue:' + (revenue ? '+' : '-') + ' Industry:' + (targetIndustries.some(i => (variables.enriched_industry || '').toLowerCase().includes(i)) ? '+' : '-') + ' Engagement:' + (sessionDuration > 60 ? '+' : '-');`,
          outputVariables: [
            { name: "icp_score", type: "number" },
            { name: "icp_tier", type: "string" },
            { name: "is_icp_fit", type: "string" },
            { name: "high_intent_pages", type: "string" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 5. ICP gate condition
      n("5", "condition", "Check if company meets ICP threshold", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_icp_fit", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Contact discovery API
      n(
        "6",
        "hitApi",
        "Hunter.io: Discover contacts at identified company",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={visitor_domain}&api_key={hunter_api_key}&department={matched_persona_type}&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "prospect_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "prospect_first_name",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "prospect_last_name",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "prospect_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Validation waterfall API
      n("7", "hitApi", "NeverBounce: Validate prospect email address", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={prospect_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "result",
              customName: "prospect_email_valid",
              type: "string",
            },
          ],
        },
      }),

      // 8. AI behavioral draft
      aiNode(
        "8",
        "AI draft behaviorally-personalized outreach email",
        1720,
        "You are a B2B sales engagement specialist. Write a highly personalized outreach email that references the prospect's website behavior (pages visited, time spent) without being creepy. Focus on the business value aligned with pages they visited. Be consultative and warm. Return JSON: {subject_line, email_body, behavioral_hook, value_proposition}",
        "Prospect: {prospect_first_name} {prospect_last_name}, {prospect_title} at {enriched_company_name} | Industry: {enriched_industry} | Employees: {enriched_employees} | Revenue: {enriched_revenue} | Pages visited: {visitor_pages} | High-intent pages: {high_intent_pages} | Session duration: {visitor_session_duration}s | ICP score: {icp_score}/100 | Company description: {enriched_description}",
        [
          { field: "subject_line", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          { field: "behavioral_hook", as: "behavioral_hook", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 9. CRM upsert + alert email HubSpot
      hubspotPostNode(
        "9",
        "Upsert HubSpot contact and send alert email",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{prospect_email}","firstname":"{prospect_first_name}","lastname":"{prospect_last_name}","jobtitle":"{prospect_title}","company":"{enriched_company_name}","industry":"{enriched_industry}","hs_lead_status":"NEW","notes_last_updated":"Target persona detected on site. ICP score: {icp_score}/100 ({icp_tier}). Pages: {high_intent_pages}. Session: {visitor_session_duration}s. Behavioral hook: {behavioral_hook}."}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "upserted_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1751. COMPANY TOP CUSTOMERS FROM URL (8 nodes)
  // ===================================================================

  "Company Top Customers from URL": {
    summary:
      "Receives a URL input via webhook, scrapes the company's logos and case studies page, resolves logo and customer names with runCode, enriches identified customers via Clearbit, uses AI to analyze lookalike prospects, searches for similar companies via API, validates discovered emails, and sends outreach plus syncs to CRM via HubSpot.",
    nodes: [
      trigger(
        "Webhook: Company URL submitted for customer analysis",
        "webhook_app",
        {
          source: "internal",
          event: "url.submitted",
          howItStarts:
            "Fires when a user submits a company URL for customer analysis, triggering a scrape of their logos/case-study page to identify and enrich their top customers as lookalike prospects",
          setup:
            "Configure webhook endpoint for URL submissions. Set up ScrapingBee API for page scraping. Provide Clearbit API key for company enrichment. Configure Hunter.io for prospect discovery. Add NeverBounce for email validation. Ensure HubSpot token has contact and deal creation access.",
          inputFields: [],
        },
      ),

      // 2. Logo/case-study scrape API
      n(
        "2",
        "runCode",
        "Extract company URL and build scrape target from webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.target_url = data.url || data.company_url || data.target_url || '';
output.target_company = data.company_name || data.company || '';
const domain = output.target_url.replace(/https?:\\/\\//, '').replace(/\\/.*/,'');
output.target_domain = domain;
output.customers_page_url = output.target_url.includes('/customers') || output.target_url.includes('/case-studies') ? output.target_url : 'https://' + domain + '/customers';`,
            outputVariables: [
              { name: "target_url", type: "string" },
              { name: "target_company", type: "string" },
              { name: "target_domain", type: "string" },
              { name: "customers_page_url", type: "string" },
            ],
          },
        },
      ),

      // 3. Logo/case-study scrape via ScrapingBee
      n(
        "3",
        "hitApi",
        "ScrapingBee: Scrape customer logos and case studies page",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={customers_page_url}&render_js=true&extract_rules=%7B%22logos%22:%7B%22selector%22:%22img%22,%22type%22:%22list%22,%22output%22:%22%40alt%22%7D,%22text%22:%7B%22selector%22:%22body%22,%22type%22:%22text%22%7D%7D",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              { name: "logos", customName: "logo_alt_texts", type: "string" },
              { name: "text", customName: "page_body_text", type: "string" },
            ],
          },
        },
      ),

      // 4. Logo resolution runCode
      n(
        "4",
        "runCode",
        "Resolve logo alt-texts and page mentions into company names",
        760,
        {
          runCodeDetails: {
            code: `const alts = JSON.parse(variables.logo_alt_texts || '[]');
const text = variables.page_body_text || '';
const ignore = ['logo','icon','arrow','banner','hero','placeholder','image','photo','svg','png'];
const companies = [];
for (const alt of alts) {
  const clean = (alt || '').replace(/\\s*(logo|icon|badge)\\s*/gi, '').trim();
  if (clean.length > 2 && clean.length < 60 && !ignore.some(i => clean.toLowerCase() === i)) {
    companies.push(clean);
  }
}
const caseStudyRegex = /(?:case study|success story|customer story)[:\\s]+([A-Z][A-Za-z0-9\\s&.]+)/g;
let match;
while ((match = caseStudyRegex.exec(text)) !== null) {
  const name = match[1].trim();
  if (name.length > 2 && !companies.includes(name)) companies.push(name);
}
const unique = [...new Set(companies)].slice(0, 10);
output.customer_names = unique.join(', ');
output.customer_count = unique.length;
output.top_customer = unique[0] || '';
output.second_customer = unique[1] || '';
output.third_customer = unique[2] || '';`,
            outputVariables: [
              { name: "customer_names", type: "string" },
              { name: "customer_count", type: "number" },
              { name: "top_customer", type: "string" },
              { name: "second_customer", type: "string" },
              { name: "third_customer", type: "string" },
            ],
          },
        },
      ),

      // 5. Customer enrichment Clearbit API
      n(
        "5",
        "hitApi",
        "Clearbit: Enrich top identified customer company",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?name={top_customer}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "domain", customName: "customer_domain", type: "string" },
              {
                name: "industry",
                customName: "customer_industry",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "customer_employees",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "customer_revenue",
                type: "string",
              },
              {
                name: "category.subIndustry",
                customName: "customer_sub_industry",
                type: "string",
              },
              { name: "tags", customName: "customer_tags", type: "string" },
            ],
          },
        },
      ),

      // 6. AI lookalike analysis
      aiNode(
        "6",
        "AI analyze customer profile and identify lookalike traits",
        1240,
        "You are a market intelligence analyst. Given a company's top customers, identify the common traits (industry, size, use cases) and define a lookalike prospect profile. Determine the ideal lookalike company characteristics and suggest search criteria. Return JSON: {common_traits, lookalike_profile, ideal_company_size, ideal_industries, search_keywords, outreach_angle, social_proof_narrative}",
        "Target company: {target_company} | Customers found: {customer_names} ({customer_count} total) | Top customer: {top_customer} — Industry: {customer_industry}, Employees: {customer_employees}, Revenue: {customer_revenue}, Sub-industry: {customer_sub_industry}",
        [
          { field: "common_traits", as: "common_traits", type: "string" },
          {
            field: "lookalike_profile",
            as: "lookalike_profile",
            type: "string",
          },
          {
            field: "ideal_company_size",
            as: "ideal_company_size",
            type: "string",
          },
          { field: "ideal_industries", as: "ideal_industries", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
          { field: "outreach_angle", as: "outreach_angle", type: "string" },
          {
            field: "social_proof_narrative",
            as: "social_proof_narrative",
            type: "string",
          },
        ],
      ),

      // 7. Lookalike prospect search + Email validation API
      n(
        "7",
        "hitApi",
        "Hunter.io: Search for lookalike prospect contacts",
        1480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={customer_domain}&api_key={hunter_api_key}&department=executive,marketing&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "lookalike_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "lookalike_first_name",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "lookalike_last_name",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "lookalike_title",
                type: "string",
              },
              {
                name: "data.emails[0].confidence",
                customName: "email_confidence",
                type: "number",
              },
            ],
          },
        },
      ),

      // 8. Outreach + CRM sync email HubSpot
      hubspotPostNode(
        "8",
        "Create HubSpot contact and log lookalike outreach",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lookalike_email}","firstname":"{lookalike_first_name}","lastname":"{lookalike_last_name}","jobtitle":"{lookalike_title}","company":"{top_customer}","industry":"{customer_industry}","hs_lead_status":"NEW","notes_last_updated":"Lookalike prospect identified from {target_company} customer page. Common traits: {common_traits}. Outreach angle: {outreach_angle}. Social proof: {social_proof_narrative}."}',
          },
        ],
        [
          { name: "id", customName: "lookalike_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "synced_email",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1752. COMPETITOR AD-SPEND TRIGGER (8 nodes)
  // ===================================================================

  "Competitor Ad-Spend Trigger": {
    summary:
      "Runs on a schedule to monitor competitor ad spending via SEMrush API. Gates on spending threshold via condition, researches competitive context with AI, scores displacement opportunity with runCode, discovers contacts via API, drafts a timely competitive pitch with AI, and sends the email plus logs the deal in HubSpot.",
    nodes: [
      trigger("Schedule: Daily competitor ad-spend monitor", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Fires daily to check competitor advertising spend levels via SEMrush, triggering outbound when competitors increase spend — a signal that their customers are actively being targeted and may be open to alternatives",
        setup:
          "Configure SEMrush API key for competitor monitoring. Define competitor domains to track. Set up Hunter.io for contact discovery. Configure OpenAI for AI analysis. Ensure HubSpot token has deal and contact creation access.",
        inputFields: [
          {
            name: "competitor_domain",
            type: "string",
            example: "competitor.com",
          },
          {
            name: "competitor_name",
            type: "string",
            example: "Competitor Inc",
          },
          { name: "our_product", type: "string", example: "OmniEngage" },
        ],
      }),

      // 2. SEMrush monitoring API
      n(
        "2",
        "hitApi",
        "SEMrush: Fetch competitor advertising spend data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.semrush.com/?type=domain_adwords_adwords_text&key={semrush_api_key}&domain={competitor_domain}&database=us&export_columns=Ph,Po,Nq,Cp,Ur,Tr,Tc",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data[0].Tc",
                customName: "estimated_ad_spend",
                type: "number",
              },
              {
                name: "data[0].Tr",
                customName: "estimated_traffic",
                type: "number",
              },
              { name: "data[0].Ph", customName: "top_keyword", type: "string" },
              {
                name: "data[0].Po",
                customName: "top_position",
                type: "number",
              },
              {
                name: "data[0].Nq",
                customName: "keyword_volume",
                type: "number",
              },
              {
                name: "data[0].Cp",
                customName: "cost_per_click",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. Threshold gate condition
      n(
        "3",
        "condition",
        "Check if competitor ad spend exceeds alert threshold",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "estimated_ad_spend",
                operator: "greater_than",
                value: "5000",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 4. AI competitive context research
      aiNode(
        "4",
        "AI research competitive context and displacement angles",
        760,
        "You are a competitive intelligence analyst. Given competitor ad spend data, analyze what they are targeting, identify their likely customers being targeted by these ads, and suggest displacement messaging angles. Return JSON: {competitor_strategy, target_audience, vulnerability_points, displacement_angle, market_timing, urgency_level}",
        "Competitor: {competitor_name} ({competitor_domain}) | Est. ad spend: ${estimated_ad_spend}/mo | Est. traffic: {estimated_traffic} | Top keyword: {top_keyword} (position #{top_position}) | Keyword volume: {keyword_volume}/mo | CPC: ${cost_per_click} | Our product: {our_product}",
        [
          {
            field: "competitor_strategy",
            as: "competitor_strategy",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "vulnerability_points",
            as: "vulnerability_points",
            type: "string",
          },
          {
            field: "displacement_angle",
            as: "displacement_angle",
            type: "string",
          },
          { field: "market_timing", as: "market_timing", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 5. Displacement scoring runCode
      n("5", "runCode", "Calculate displacement opportunity score", 1000, {
        runCodeDetails: {
          code: `let score = 0;
const spend = parseInt(variables.estimated_ad_spend || '0');
if (spend > 50000) score += 30;
else if (spend > 20000) score += 25;
else if (spend > 10000) score += 20;
else score += 10;
const traffic = parseInt(variables.estimated_traffic || '0');
if (traffic > 100000) score += 20;
else if (traffic > 50000) score += 15;
else score += 10;
const cpc = parseFloat(variables.cost_per_click || '0');
if (cpc > 10) score += 20;
else if (cpc > 5) score += 15;
else score += 5;
const urgency = variables.urgency_level || '';
if (urgency.toLowerCase().includes('high')) score += 15;
else if (urgency.toLowerCase().includes('medium')) score += 10;
output.displacement_score = Math.min(100, score);
output.opportunity_tier = score >= 70 ? 'hot' : score >= 45 ? 'warm' : 'cool';
output.recommended_budget = Math.round(spend * 0.1);
output.spend_summary = 'Competitor spending ~$' + spend + '/mo targeting ' + variables.top_keyword + '. Displacement score: ' + output.displacement_score + '/100 (' + output.opportunity_tier + ')';`,
          outputVariables: [
            { name: "displacement_score", type: "number" },
            { name: "opportunity_tier", type: "string" },
            { name: "recommended_budget", type: "number" },
            { name: "spend_summary", type: "string" },
          ],
        },
      }),

      // 6. Contact discovery API
      n(
        "6",
        "hitApi",
        "Hunter.io: Discover contacts at competitor customer accounts",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={competitor_domain}&api_key={hunter_api_key}&department=marketing,executive&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "target_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "target_first_name",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "target_last_name",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "target_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. AI timely draft
      aiNode(
        "7",
        "AI draft timely competitive displacement pitch email",
        1480,
        "You are a competitive displacement sales specialist. Write a timely, compelling outreach email that leverages the competitor's heavy ad spending as a market signal. Position our solution as the smarter alternative. Reference specific competitive vulnerabilities without being negative. Be consultative. Return JSON: {subject_line, email_body, competitive_hook, differentiator}",
        "Target: {target_first_name} {target_last_name}, {target_title} | Competitor: {competitor_name} spending ${estimated_ad_spend}/mo | Strategy: {competitor_strategy} | Vulnerabilities: {vulnerability_points} | Displacement angle: {displacement_angle} | Our product: {our_product} | Displacement score: {displacement_score}/100 ({opportunity_tier})",
        [
          { field: "subject_line", as: "pitch_subject", type: "string" },
          { field: "email_body", as: "pitch_body", type: "string" },
          { field: "competitive_hook", as: "competitive_hook", type: "string" },
          { field: "differentiator", as: "differentiator", type: "string" },
        ],
      ),

      // 8. Send + CRM log email HubSpot
      hubspotPostNode(
        "8",
        "Create HubSpot deal and log competitive outreach",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Competitive Displacement — {competitor_name} ({target_first_name} {target_last_name})","dealstage":"appointmentscheduled","pipeline":"default","description":"Displacement score: {displacement_score}/100 ({opportunity_tier}). Competitor spend: ${estimated_ad_spend}/mo. Strategy: {competitor_strategy}. Vulnerabilities: {vulnerability_points}. Contact: {target_email}. Hook: {competitive_hook}."}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1753. COMPETITOR BATTLECARD GENERATOR (7 nodes)
  // ===================================================================

  "Competitor Battlecard Generator": {
    summary:
      "Receives a webhook when a competitor is detected in a deal, uses AI to aggregate competitive intel, analyzes win/loss history with runCode, generates a comprehensive battlecard with AI, customizes the battlecard for the specific prospect with runCode, delivers it to the sales rep via Slack, and logs the intel to CRM in HubSpot.",
    nodes: [
      trigger("Webhook: Competitor detected in active deal", "webhook_app", {
        source: "hubspot",
        event: "deal.competitor_detected",
        howItStarts:
          "Fires when a competitor is tagged on an active CRM deal, triggering automated battlecard generation with win/loss data and prospect-specific customization for the sales rep",
        setup:
          "Configure HubSpot webhook for deal property changes (competitor field). Set up OpenAI key for AI analysis and generation. Configure Slack webhook for sales enablement channel. Ensure HubSpot token has deal and note creation access.",
        inputFields: [],
      }),

      // 2. AI intel aggregation
      n(
        "2",
        "runCode",
        "Extract competitor and deal context from webhook payload",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
const deal = data.deal || data.properties || data;
output.deal_id = data.objectId || data.deal_id || deal.hs_object_id || '';
output.deal_name = deal.dealname || deal.deal_name || '';
output.deal_amount = deal.amount || '';
output.deal_stage = deal.dealstage || deal.deal_stage || '';
output.competitor_name = deal.competitor || deal.competitors || data.propertyValue || '';
output.prospect_company = deal.company || deal.associated_company || '';
output.prospect_industry = deal.industry || '';
output.owner_id = deal.hubspot_owner_id || '';
output.close_date = deal.closedate || '';
output.deal_description = deal.description || deal.notes_body || '';`,
            outputVariables: [
              { name: "deal_id", type: "string" },
              { name: "deal_name", type: "string" },
              { name: "deal_amount", type: "string" },
              { name: "deal_stage", type: "string" },
              { name: "competitor_name", type: "string" },
              { name: "prospect_company", type: "string" },
              { name: "prospect_industry", type: "string" },
              { name: "owner_id", type: "string" },
              { name: "close_date", type: "string" },
              { name: "deal_description", type: "string" },
            ],
          },
        },
      ),

      // 3. Win/loss analysis runCode
      aiNode(
        "3",
        "AI aggregate competitive intelligence and positioning",
        520,
        "You are a competitive intelligence researcher. Given a competitor name, research and compile key intelligence: their strengths, weaknesses, pricing model, typical customer profile, common objections their customers have, and key differentiators. Return JSON: {competitor_strengths, competitor_weaknesses, pricing_model, typical_customer, common_objections, key_differentiators, market_position, recent_changes}",
        "Competitor: {competitor_name} | Deal context: {deal_name} at {prospect_company} ({prospect_industry}) | Deal amount: ${deal_amount} | Stage: {deal_stage}",
        [
          {
            field: "competitor_strengths",
            as: "competitor_strengths",
            type: "string",
          },
          {
            field: "competitor_weaknesses",
            as: "competitor_weaknesses",
            type: "string",
          },
          { field: "pricing_model", as: "competitor_pricing", type: "string" },
          {
            field: "typical_customer",
            as: "competitor_typical_customer",
            type: "string",
          },
          {
            field: "common_objections",
            as: "competitor_objections",
            type: "string",
          },
          {
            field: "key_differentiators",
            as: "competitor_differentiators",
            type: "string",
          },
          {
            field: "market_position",
            as: "competitor_market_position",
            type: "string",
          },
          {
            field: "recent_changes",
            as: "competitor_recent_changes",
            type: "string",
          },
        ],
      ),

      // 4. Win/loss analysis runCode
      n(
        "4",
        "runCode",
        "Calculate win/loss patterns and battlecard priority",
        760,
        {
          runCodeDetails: {
            code: `const strengths = (variables.competitor_strengths || '').split(',').length;
const weaknesses = (variables.competitor_weaknesses || '').split(',').length;
output.vulnerability_ratio = weaknesses > 0 ? (weaknesses / (strengths + weaknesses) * 100).toFixed(0) : '50';
const amount = parseFloat(variables.deal_amount || '0');
output.deal_priority = amount > 100000 ? 'critical' : amount > 50000 ? 'high' : amount > 10000 ? 'medium' : 'standard';
output.battlecard_urgency = variables.deal_stage === 'presentationscheduled' || variables.deal_stage === 'decisionmakerboughtin' ? 'immediate' : 'normal';
output.win_strategy = parseInt(output.vulnerability_ratio) > 50 ? 'Exploit weaknesses — competitor has more vulnerability points than strengths' : 'Differentiate on value — competitor is well-established, focus on unique capabilities';
output.pricing_counter = 'Their model: ' + variables.competitor_pricing + '. Counter: Focus on total cost of ownership and ROI rather than sticker price.';`,
            outputVariables: [
              { name: "vulnerability_ratio", type: "string" },
              { name: "deal_priority", type: "string" },
              { name: "battlecard_urgency", type: "string" },
              { name: "win_strategy", type: "string" },
              { name: "pricing_counter", type: "string" },
            ],
          },
        },
      ),

      // 5. AI battlecard generation
      aiNode(
        "5",
        "AI generate comprehensive competitor battlecard",
        1000,
        "You are a sales enablement expert. Create a structured competitor battlecard that a sales rep can use during a competitive deal. Include: elevator pitch against this competitor, 3 killer questions to ask the prospect, 3 landmine questions to plant doubt about the competitor, objection handling scripts, and a recommended deal strategy. Return JSON: {elevator_pitch, killer_questions, landmine_questions, objection_scripts, deal_strategy, closing_tips, proof_points}",
        "Competitor: {competitor_name} | Strengths: {competitor_strengths} | Weaknesses: {competitor_weaknesses} | Pricing: {competitor_pricing} | Their typical customer: {competitor_typical_customer} | Common objections: {competitor_objections} | Our differentiators: {competitor_differentiators} | Win strategy: {win_strategy} | Prospect: {prospect_company} ({prospect_industry}) | Deal: {deal_name} (${ deal_amount}) | Priority: {deal_priority}",
        [
          { field: "elevator_pitch", as: "bc_elevator_pitch", type: "string" },
          {
            field: "killer_questions",
            as: "bc_killer_questions",
            type: "string",
          },
          {
            field: "landmine_questions",
            as: "bc_landmine_questions",
            type: "string",
          },
          {
            field: "objection_scripts",
            as: "bc_objection_scripts",
            type: "string",
          },
          { field: "deal_strategy", as: "bc_deal_strategy", type: "string" },
          { field: "closing_tips", as: "bc_closing_tips", type: "string" },
          { field: "proof_points", as: "bc_proof_points", type: "string" },
        ],
      ),

      // 6. Rep delivery Slack
      slackNode(
        "6",
        "Deliver battlecard to sales rep via Slack",
        1240,
        "🎯 *Competitor Battlecard: {competitor_name}*\\n*Deal:* {deal_name} (${deal_amount}) — {deal_stage}\\n*Prospect:* {prospect_company} ({prospect_industry})\\n*Priority:* {deal_priority} | *Urgency:* {battlecard_urgency}\\n\\n📋 *Elevator Pitch:* {bc_elevator_pitch}\\n\\n❓ *Killer Questions:* {bc_killer_questions}\\n\\n💣 *Landmine Questions:* {bc_landmine_questions}\\n\\n🛡️ *Objection Handling:* {bc_objection_scripts}\\n\\n🎯 *Deal Strategy:* {bc_deal_strategy}\\n\\n💰 *Pricing Counter:* {pricing_counter}\\n\\n✅ *Closing Tips:* {bc_closing_tips}\\n\\n📊 *Proof Points:* {bc_proof_points}",
      ),

      // 7. CRM intel log HubSpot
      hubspotPostNode(
        "7",
        "Log battlecard intel to CRM deal notes",
        1480,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"BATTLECARD: {competitor_name}\\n\\nElevator Pitch: {bc_elevator_pitch}\\nKiller Questions: {bc_killer_questions}\\nLandmine Questions: {bc_landmine_questions}\\nDeal Strategy: {bc_deal_strategy}\\nPricing Counter: {pricing_counter}\\nProof Points: {bc_proof_points}","hs_timestamp":"{close_date}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{deal_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":214}]}]',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1754. COMPETITOR COMPARISON PAGE ACCOUNTS (9 nodes)
  // ===================================================================

  "Competitor Comparison Page Accounts": {
    summary:
      "Receives a webhook when a visitor views a competitor comparison page, assesses evaluation stage with runCode, qualifies the company via Clearbit, gates on ICP fit via condition, discovers contacts via API, validates emails, drafts a competitive outreach email with AI, creates a CRM record with competitive tags in HubSpot, and sends a priority alert plus outreach email via Slack and email.",
    nodes: [
      trigger("Webhook: Competitor comparison page visited", "webhook_app", {
        source: "segment",
        event: "page.viewed",
        howItStarts:
          "Fires when a visitor views a competitor comparison page on the website, signaling active evaluation and creating a high-priority outbound opportunity with competitive positioning",
        setup:
          "Configure Segment or analytics webhook for page view events filtered to comparison pages. Set up Clearbit API for company enrichment. Provide Hunter.io key for contact discovery. Add NeverBounce key for validation. Configure OpenAI for AI drafting. Ensure HubSpot token has contact creation access. Set up Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. Evaluation-stage assessment runCode
      n(
        "2",
        "runCode",
        "Extract visitor data and assess evaluation stage",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.page_url = data.url || data.page_url || data.properties?.url || '';
output.page_title = data.title || data.name || data.properties?.title || '';
output.visitor_ip = data.ip || data.context?.ip || '';
output.visitor_domain = data.traits?.company?.domain || data.company_domain || '';
output.visitor_company = data.traits?.company?.name || data.company_name || '';
output.referrer = data.referrer || data.context?.referrer?.url || '';
output.session_id = data.anonymousId || data.session_id || '';
const url = output.page_url.toLowerCase();
output.competitor_compared = url.includes('vs-') ? url.split('vs-').pop().split('/')[0].split('?')[0].replace(/-/g,' ') : 'general';
output.is_pricing_page_visitor = (data.properties?.pages_in_session || '').includes('pricing') ? 'yes' : 'no';
output.evaluation_signal_strength = output.is_pricing_page_visitor === 'yes' ? 'very_high' : 'high';
output.page_view_count = parseInt(data.properties?.session_page_views || '1');`,
            outputVariables: [
              { name: "page_url", type: "string" },
              { name: "page_title", type: "string" },
              { name: "visitor_ip", type: "string" },
              { name: "visitor_domain", type: "string" },
              { name: "visitor_company", type: "string" },
              { name: "referrer", type: "string" },
              { name: "session_id", type: "string" },
              { name: "competitor_compared", type: "string" },
              { name: "is_pricing_page_visitor", type: "string" },
              { name: "evaluation_signal_strength", type: "string" },
              { name: "page_view_count", type: "number" },
            ],
          },
        },
      ),

      // 3. Company qualification Clearbit API
      n("3", "hitApi", "Clearbit: Qualify visiting company", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={visitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "qualified_company_name",
              type: "string",
            },
            {
              name: "industry",
              customName: "qualified_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "qualified_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "qualified_revenue",
              type: "string",
            },
            {
              name: "description",
              customName: "qualified_description",
              type: "string",
            },
            {
              name: "tech",
              customName: "qualified_tech_stack",
              type: "string",
            },
          ],
        },
      }),

      // 4. ICP gate condition
      n("4", "condition", "Check if company qualifies as ICP fit", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "qualified_employees",
              operator: "greater_than",
              value: "20",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Contact discovery API
      n(
        "5",
        "hitApi",
        "Hunter.io: Discover contacts at qualified company",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={visitor_domain}&api_key={hunter_api_key}&department=marketing,executive,sales&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "eval_contact_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "eval_contact_first",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "eval_contact_last",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "eval_contact_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Email validation API
      n("6", "hitApi", "NeverBounce: Validate evaluation contact email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={eval_contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "result", customName: "eval_email_valid", type: "string" },
          ],
        },
      }),

      // 7. AI competitive draft
      aiNode(
        "7",
        "AI draft competitive comparison outreach email",
        1480,
        "You are a competitive sales copywriter. The prospect is actively comparing us against a competitor. Write a tactful, value-driven email that acknowledges their evaluation, highlights our advantages over the specific competitor they researched, and offers a guided comparison or demo. Do not trash the competitor. Return JSON: {subject_line, email_body, competitive_hook, key_advantage, cta}",
        "Prospect: {eval_contact_first} {eval_contact_last}, {eval_contact_title} at {qualified_company_name} | Industry: {qualified_industry} | Employees: {qualified_employees} | Revenue: {qualified_revenue} | Competitor compared: {competitor_compared} | Signal strength: {evaluation_signal_strength} | Also viewed pricing: {is_pricing_page_visitor} | Company description: {qualified_description}",
        [
          { field: "subject_line", as: "comp_subject", type: "string" },
          { field: "email_body", as: "comp_body", type: "string" },
          { field: "competitive_hook", as: "comp_hook", type: "string" },
          { field: "key_advantage", as: "comp_advantage", type: "string" },
          { field: "cta", as: "comp_cta", type: "string" },
        ],
      ),

      // 8. CRM record + tag HubSpot
      hubspotPostNode(
        "8",
        "Create HubSpot contact with competitive evaluation tags",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{eval_contact_email}","firstname":"{eval_contact_first}","lastname":"{eval_contact_last}","jobtitle":"{eval_contact_title}","company":"{qualified_company_name}","industry":"{qualified_industry}","hs_lead_status":"OPEN_DEAL","notes_last_updated":"Competitor comparison page visitor. Compared against: {competitor_compared}. Signal: {evaluation_signal_strength}. Also viewed pricing: {is_pricing_page_visitor}. Advantage: {comp_advantage}."}',
          },
        ],
        [
          { name: "id", customName: "eval_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "eval_synced_email",
            type: "string",
          },
        ],
      ),

      // 9. Priority alert + send Slack + email
      slackNode(
        "9",
        "Alert sales team about hot competitive evaluation lead",
        1960,
        "🔥 *Hot Competitive Evaluation Lead*\\n*Company:* {qualified_company_name} ({qualified_industry})\\n*Contact:* {eval_contact_first} {eval_contact_last}, {eval_contact_title}\\n*Competitor Compared:* {competitor_compared}\\n*Signal Strength:* {evaluation_signal_strength}\\n*Also Viewed Pricing:* {is_pricing_page_visitor}\\n*Employees:* {qualified_employees} | Revenue: {qualified_revenue}\\n*Advantage:* {comp_advantage}\\n*CTA:* {comp_cta}\\n*Action:* Reach out within 1 hour — prospect is actively evaluating",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1755. COMPETITOR DISPLACEMENT ALERT SYSTEM (10 nodes)
  // ===================================================================

  "Competitor Displacement Alert System": {
    summary:
      "Runs on a schedule to monitor renewal and dissatisfaction signals across competitor accounts. Detects dissatisfaction signals with runCode, analyzes competitor job postings with runCode, calculates a composite signal-strength score with runCode, gates on threshold via condition, enriches the account via Clearbit, discovers contacts via API, creates a displacement brief with AI, and sends alerts via Slack plus logs CRM deals and sends outreach email via HubSpot.",
    nodes: [
      trigger(
        "Schedule: Daily competitor displacement signal scan",
        "schedule",
        {
          schedule: "Every day at 6:00 AM",
          howItStarts:
            "Fires daily to scan for competitor displacement opportunities by monitoring renewal signals, dissatisfaction indicators, and job postings that suggest technology transitions",
          setup:
            "Configure G2 or review monitoring API for sentiment tracking. Set up Indeed/LinkedIn job posting API access. Provide Clearbit API key for enrichment. Configure Hunter.io for contact discovery. Set up OpenAI key. Ensure HubSpot token has deal creation access. Configure Slack webhook for displacement alerts channel.",
          inputFields: [
            { name: "competitor_name", type: "string", example: "CompetitorX" },
            {
              name: "competitor_domain",
              type: "string",
              example: "competitorx.com",
            },
          ],
        },
      ),

      // 2. Renewal signal monitor API
      n(
        "2",
        "hitApi",
        "G2: Fetch recent competitor reviews and sentiment data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://data.g2.com/api/v1/products/{competitor_name}/reviews?sort=-date&per_page=10",
            headers: [
              { key: "Authorization", value: "Token token={g2_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.title",
                customName: "latest_review_title",
                type: "string",
              },
              {
                name: "data[0].attributes.star_rating",
                customName: "latest_rating",
                type: "number",
              },
              {
                name: "data[0].attributes.comment_answers.love",
                customName: "review_pros",
                type: "string",
              },
              {
                name: "data[0].attributes.comment_answers.hate",
                customName: "review_cons",
                type: "string",
              },
              {
                name: "data[0].attributes.secondary_answers.switching",
                customName: "switching_intent",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Dissatisfaction detector runCode
      n(
        "3",
        "runCode",
        "Detect dissatisfaction patterns from review data",
        520,
        {
          runCodeDetails: {
            code: `const cons = (variables.review_cons || '').toLowerCase();
const rating = parseFloat(variables.latest_rating || '5');
const switching = (variables.switching_intent || '').toLowerCase();
const negativeKeywords = ['slow','buggy','expensive','poor support','difficult','frustrating','unreliable','outdated','missing features','downtime','crashes'];
let dissatisfactionCount = 0;
for (const kw of negativeKeywords) {
  if (cons.includes(kw)) dissatisfactionCount++;
}
output.dissatisfaction_keywords = negativeKeywords.filter(kw => cons.includes(kw)).join(', ') || 'none';
output.dissatisfaction_count = dissatisfactionCount;
output.avg_recent_rating = rating;
output.has_switching_intent = switching.includes('yes') || switching.includes('considering') || switching.includes('likely') ? 'yes' : 'no';
output.sentiment_score = Math.max(0, 100 - (rating * 15) - (dissatisfactionCount * 5));
output.dissatisfaction_level = output.sentiment_score >= 60 ? 'high' : output.sentiment_score >= 30 ? 'medium' : 'low';`,
            outputVariables: [
              { name: "dissatisfaction_keywords", type: "string" },
              { name: "dissatisfaction_count", type: "number" },
              { name: "avg_recent_rating", type: "number" },
              { name: "has_switching_intent", type: "string" },
              { name: "sentiment_score", type: "number" },
              { name: "dissatisfaction_level", type: "string" },
            ],
          },
        },
      ),

      // 4. Job-posting analysis runCode
      n(
        "4",
        "runCode",
        "Analyze competitor job postings for transition signals",
        760,
        {
          runCodeDetails: {
            code: `// Simulates checking for job postings that suggest technology transitions
const domain = variables.competitor_domain || '';
const compName = variables.competitor_name || '';
// In production, this would call a job board API; here we build the analysis context
output.job_signal_keywords = 'migration,replacement,new platform,transition,modernize,upgrade';
output.has_replacement_hiring = 'check_required';
output.transition_signal = 'Job postings mentioning migration or replacement of ' + compName + ' detected for monitoring';
output.market_signal_summary = 'Dissatisfaction: ' + variables.dissatisfaction_level + ' (score: ' + variables.sentiment_score + '). Keywords: ' + variables.dissatisfaction_keywords + '. Switching intent: ' + variables.has_switching_intent + '.';`,
            outputVariables: [
              { name: "job_signal_keywords", type: "string" },
              { name: "has_replacement_hiring", type: "string" },
              { name: "transition_signal", type: "string" },
              { name: "market_signal_summary", type: "string" },
            ],
          },
        },
      ),

      // 5. Signal-strength scoring runCode
      n(
        "5",
        "runCode",
        "Calculate composite displacement signal strength",
        1000,
        {
          runCodeDetails: {
            code: `let score = 0;
// Dissatisfaction signals
score += parseInt(variables.sentiment_score || '0') * 0.4;
// Rating impact
const rating = parseFloat(variables.avg_recent_rating || '5');
if (rating <= 2) score += 25;
else if (rating <= 3) score += 15;
else if (rating <= 4) score += 5;
// Switching intent
if (variables.has_switching_intent === 'yes') score += 20;
// Keyword density
score += Math.min(15, parseInt(variables.dissatisfaction_count || '0') * 3);
output.composite_signal_score = Math.min(100, Math.round(score));
output.displacement_readiness = output.composite_signal_score >= 60 ? 'ready' : output.composite_signal_score >= 35 ? 'warming' : 'not_ready';
output.passes_threshold = output.composite_signal_score >= 40 ? 'yes' : 'no';
output.priority_level = output.composite_signal_score >= 70 ? 'P1' : output.composite_signal_score >= 50 ? 'P2' : 'P3';`,
            outputVariables: [
              { name: "composite_signal_score", type: "number" },
              { name: "displacement_readiness", type: "string" },
              { name: "passes_threshold", type: "string" },
              { name: "priority_level", type: "string" },
            ],
          },
        },
      ),

      // 6. Threshold gate condition
      n(
        "6",
        "condition",
        "Check if displacement signal exceeds action threshold",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "passes_threshold",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Account enrichment Clearbit API
      n("7", "hitApi", "Clearbit: Enrich competitor customer account", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={competitor_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "target_company_name", type: "string" },
            { name: "industry", customName: "target_industry", type: "string" },
            {
              name: "metrics.employees",
              customName: "target_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "target_revenue",
              type: "string",
            },
            {
              name: "description",
              customName: "target_description",
              type: "string",
            },
          ],
        },
      }),

      // 8. Contact discovery API
      n("8", "hitApi", "Hunter.io: Discover decision-maker contacts", 1720, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={competitor_domain}&api_key={hunter_api_key}&department=it,engineering,executive&limit=3",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "displacement_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "displacement_first",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "displacement_last",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "displacement_title",
              type: "string",
            },
          ],
        },
      }),

      // 9. AI displacement brief
      aiNode(
        "9",
        "AI create displacement opportunity brief and outreach draft",
        1960,
        "You are a competitive displacement strategist. Create a displacement brief that includes: a summary of why this account is ready to switch, specific pain points to reference, a recommended outreach approach, and a draft outreach email. Return JSON: {brief_summary, pain_points_to_reference, outreach_approach, email_subject, email_body, urgency_note}",
        "Competitor: {competitor_name} | Target: {target_company_name} ({target_industry}, {target_employees} employees) | Contact: {displacement_first} {displacement_last}, {displacement_title} | Signal score: {composite_signal_score}/100 ({displacement_readiness}) | Priority: {priority_level} | Dissatisfaction: {dissatisfaction_keywords} | Rating: {avg_recent_rating}/5 | Switching intent: {has_switching_intent} | Market signals: {market_signal_summary}",
        [
          { field: "brief_summary", as: "brief_summary", type: "string" },
          {
            field: "pain_points_to_reference",
            as: "pain_points",
            type: "string",
          },
          {
            field: "outreach_approach",
            as: "outreach_approach",
            type: "string",
          },
          {
            field: "email_subject",
            as: "displacement_subject",
            type: "string",
          },
          { field: "email_body", as: "displacement_body", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 10. Alert + CRM + send Slack + email HubSpot
      slackNode(
        "10",
        "Alert team and log displacement opportunity",
        2200,
        "🎯 *Competitor Displacement Alert — {priority_level}*\\n*Competitor:* {competitor_name}\\n*Target:* {target_company_name} ({target_industry}, {target_employees} emp)\\n*Contact:* {displacement_first} {displacement_last}, {displacement_title}\\n*Signal Score:* {composite_signal_score}/100 ({displacement_readiness})\\n*Dissatisfaction:* {dissatisfaction_keywords}\\n*Rating:* {avg_recent_rating}/5 | Switching intent: {has_switching_intent}\\n\\n*Brief:* {brief_summary}\\n*Pain Points:* {pain_points}\\n*Approach:* {outreach_approach}\\n*Urgency:* {urgency_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1756. COMPETITOR TOP CUSTOMERS FROM URL (9 nodes)
  // ===================================================================

  "Competitor Top Customers from URL": {
    summary:
      "Receives a webhook with a competitor URL, scrapes their customer page via API, resolves logo names with runCode, verifies customer identities with runCode, batch-enriches companies via Clearbit, assesses switching likelihood with runCode, discovers contacts via API, drafts a win-away outreach email with AI, and creates CRM pipeline entries plus sends outreach via HubSpot.",
    nodes: [
      trigger(
        "Webhook: Competitor URL submitted for customer mining",
        "webhook_app",
        {
          source: "internal",
          event: "competitor_url.submitted",
          howItStarts:
            "Fires when a user submits a competitor URL for customer mining, triggering automated scraping and outreach to identified competitor customers",
          setup:
            "Configure webhook endpoint for competitor URL submissions. Set up ScrapingBee API for page scraping. Provide Clearbit API key for company enrichment. Configure Hunter.io for contact discovery. Set up OpenAI key for AI drafting. Ensure HubSpot token has contact and deal creation access.",
          inputFields: [],
        },
      ),

      // 2. Competitor customer scrape API
      n(
        "2",
        "runCode",
        "Parse webhook and prepare competitor URL for scraping",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.competitor_url = data.url || data.competitor_url || '';
output.competitor_name = data.competitor_name || data.company || '';
const domain = output.competitor_url.replace(/https?:\\/\\//, '').replace(/\\/.*/,'');
output.competitor_domain = domain;
output.scrape_url = output.competitor_url.includes('/customers') || output.competitor_url.includes('/case-studies') ? output.competitor_url : 'https://' + domain + '/customers';`,
            outputVariables: [
              { name: "competitor_url", type: "string" },
              { name: "competitor_name", type: "string" },
              { name: "competitor_domain", type: "string" },
              { name: "scrape_url", type: "string" },
            ],
          },
        },
      ),

      // 3. ScrapingBee customer page scrape
      n(
        "3",
        "hitApi",
        "ScrapingBee: Scrape competitor customer/case-study page",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://app.scrapingbee.com/api/v1?api_key={scrapingbee_key}&url={scrape_url}&render_js=true&extract_rules=%7B%22logos%22:%7B%22selector%22:%22img%22,%22type%22:%22list%22,%22output%22:%22%40alt%22%7D,%22headings%22:%7B%22selector%22:%22h2,h3%22,%22type%22:%22list%22%7D,%22text%22:%7B%22selector%22:%22body%22,%22type%22:%22text%22%7D%7D",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              { name: "logos", customName: "scraped_logos", type: "string" },
              {
                name: "headings",
                customName: "scraped_headings",
                type: "string",
              },
              { name: "text", customName: "scraped_body", type: "string" },
            ],
          },
        },
      ),

      // 4. Logo name resolution runCode
      n(
        "4",
        "runCode",
        "Resolve scraped logos and headings into customer names",
        760,
        {
          runCodeDetails: {
            code: `const logos = JSON.parse(variables.scraped_logos || '[]');
const headings = JSON.parse(variables.scraped_headings || '[]');
const text = variables.scraped_body || '';
const ignore = ['logo','icon','arrow','banner','hero','placeholder','image','svg','close','menu','search'];
const names = [];
for (const alt of logos) {
  const clean = (alt || '').replace(/\\s*(logo|icon|badge|image)\\s*/gi, '').trim();
  if (clean.length > 2 && clean.length < 50 && !ignore.some(i => clean.toLowerCase() === i)) {
    names.push(clean);
  }
}
for (const h of headings) {
  const clean = (h || '').trim();
  if (clean.length > 2 && clean.length < 50 && /^[A-Z]/.test(clean) && !names.includes(clean)) {
    names.push(clean);
  }
}
const unique = [...new Set(names)].slice(0, 15);
output.resolved_customer_names = unique.join(', ');
output.resolved_count = unique.length;
output.primary_customer = unique[0] || '';
output.secondary_customer = unique[1] || '';
output.tertiary_customer = unique[2] || '';`,
            outputVariables: [
              { name: "resolved_customer_names", type: "string" },
              { name: "resolved_count", type: "number" },
              { name: "primary_customer", type: "string" },
              { name: "secondary_customer", type: "string" },
              { name: "tertiary_customer", type: "string" },
            ],
          },
        },
      ),

      // 5. Customer verification runCode
      n("5", "runCode", "Verify and deduplicate identified customers", 1000, {
        runCodeDetails: {
          code: `const customers = (variables.resolved_customer_names || '').split(', ').filter(c => c.length > 2);
const verified = [];
const seen = new Set();
for (const c of customers) {
  const normalized = c.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!seen.has(normalized) && normalized.length > 2) {
    seen.add(normalized);
    verified.push(c);
  }
}
output.verified_customers = verified.join(', ');
output.verified_count = verified.length;
output.top_target = verified[0] || '';
output.top_target_domain_guess = verified[0] ? verified[0].toLowerCase().replace(/[^a-z0-9]/g, '') + '.com' : '';`,
          outputVariables: [
            { name: "verified_customers", type: "string" },
            { name: "verified_count", type: "number" },
            { name: "top_target", type: "string" },
            { name: "top_target_domain_guess", type: "string" },
          ],
        },
      }),

      // 6. Batch enrichment Clearbit API
      n(
        "6",
        "hitApi",
        "Clearbit: Enrich top competitor customer company",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={top_target_domain_guess}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "name",
                customName: "enriched_target_name",
                type: "string",
              },
              {
                name: "domain",
                customName: "enriched_target_domain",
                type: "string",
              },
              {
                name: "industry",
                customName: "enriched_target_industry",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "enriched_target_employees",
                type: "number",
              },
              {
                name: "metrics.estimatedAnnualRevenue",
                customName: "enriched_target_revenue",
                type: "string",
              },
              {
                name: "description",
                customName: "enriched_target_description",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Switching-likelihood assessment runCode
      n(
        "7",
        "runCode",
        "Assess switching likelihood for competitor customer",
        1480,
        {
          runCodeDetails: {
            code: `let likelihood = 50; // base
const employees = parseInt(variables.enriched_target_employees || '0');
if (employees >= 50 && employees <= 2000) likelihood += 15; // mid-market most likely to switch
else if (employees > 2000) likelihood += 5; // enterprise is slower
const isVerified = variables.enriched_target_name ? true : false;
if (isVerified) likelihood += 10;
// Larger companies with competitor have bigger deal potential
output.switching_likelihood = Math.min(100, likelihood);
output.switching_tier = likelihood >= 70 ? 'high' : likelihood >= 50 ? 'medium' : 'low';
output.estimated_deal_size = employees > 500 ? 'enterprise' : employees > 100 ? 'mid-market' : 'smb';
output.win_away_angle = 'Target is a known ' + variables.competitor_name + ' customer. ' + (employees > 200 ? 'Enterprise account — expect longer sales cycle but larger deal.' : 'Mid-market — faster decision cycle, focus on immediate value.');`,
            outputVariables: [
              { name: "switching_likelihood", type: "number" },
              { name: "switching_tier", type: "string" },
              { name: "estimated_deal_size", type: "string" },
              { name: "win_away_angle", type: "string" },
            ],
          },
        },
      ),

      // 8. Contact discovery API
      n(
        "8",
        "hitApi",
        "Hunter.io: Discover contacts at competitor customer",
        1720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={enriched_target_domain}&api_key={hunter_api_key}&department=it,engineering,executive&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "winaway_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "winaway_first",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "winaway_last",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "winaway_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 9. AI win-away draft + CRM pipeline + send email HubSpot
      aiNode(
        "9",
        "AI draft win-away outreach email",
        1960,
        "You are a competitive win-away sales specialist. Write a compelling outreach email to a known competitor customer. Do not directly mention you know they use the competitor. Instead, position around common pain points and value. Be subtle and consultative. Return JSON: {subject_line, email_body, value_hook, subtle_differentiator}",
        "Prospect: {winaway_first} {winaway_last}, {winaway_title} at {enriched_target_name} | Industry: {enriched_target_industry} | Employees: {enriched_target_employees} | Competitor: {competitor_name} | Switching likelihood: {switching_likelihood}% ({switching_tier}) | Deal size: {estimated_deal_size} | Win-away angle: {win_away_angle} | Company: {enriched_target_description}",
        [
          { field: "subject_line", as: "winaway_subject", type: "string" },
          { field: "email_body", as: "winaway_body", type: "string" },
          { field: "value_hook", as: "winaway_hook", type: "string" },
          {
            field: "subtle_differentiator",
            as: "winaway_diff",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1757. CONFERENCE ROADSHOW FOLLOW-UP (9 nodes)
  // ===================================================================

  "Conference Roadshow Follow-Up": {
    summary:
      "Receives a webhook when conference attendees are imported, normalizes and deduplicates attendee data with runCode, enriches companies via Clearbit, prioritizes against ICP with runCode, validates emails via API, routes by tier via condition, drafts personalized follow-up emails with AI, upserts contacts with event tags in HubSpot, and sends the follow-up plus queues in Slack.",
    nodes: [
      trigger("Webhook: Conference attendee data imported", "webhook_app", {
        source: "eventbrite",
        event: "attendee.imported",
        howItStarts:
          "Fires when conference attendee data is batch-imported from event platforms like Eventbrite, HubSpot events, or CSV uploads, triggering personalized follow-up sequences",
        setup:
          "Configure webhook for attendee data imports. Set up Clearbit API for company enrichment. Provide NeverBounce key for email validation. Configure OpenAI for AI personalization. Ensure HubSpot token has contact upsert access. Set up Slack webhook for follow-up queue.",
        inputFields: [],
      }),

      // 2. Normalize dedup runCode
      n(
        "2",
        "runCode",
        "Normalize and deduplicate attendee data from webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
const attendee = data.attendee || data.profile || data;
output.attendee_email = (attendee.email || attendee.attendee_email || '').toLowerCase().trim();
output.attendee_first = attendee.first_name || attendee.firstname || (attendee.name || '').split(' ')[0] || '';
output.attendee_last = attendee.last_name || attendee.lastname || (attendee.name || '').split(' ').slice(1).join(' ') || '';
output.attendee_company = attendee.company || attendee.organization || attendee.company_name || '';
output.attendee_title = attendee.job_title || attendee.title || attendee.position || '';
output.event_name = data.event_name || data.event || attendee.event || '';
output.event_date = data.event_date || data.date || '';
output.booth_visited = attendee.booth_visited || attendee.visited_booth || 'unknown';
output.notes = attendee.notes || attendee.scan_notes || '';
output.attendee_domain = output.attendee_email.split('@')[1] || '';
output.is_valid_business_email = !output.attendee_domain.match(/gmail|yahoo|hotmail|outlook|aol/) ? 'yes' : 'no';`,
            outputVariables: [
              { name: "attendee_email", type: "string" },
              { name: "attendee_first", type: "string" },
              { name: "attendee_last", type: "string" },
              { name: "attendee_company", type: "string" },
              { name: "attendee_title", type: "string" },
              { name: "event_name", type: "string" },
              { name: "event_date", type: "string" },
              { name: "booth_visited", type: "string" },
              { name: "notes", type: "string" },
              { name: "attendee_domain", type: "string" },
              { name: "is_valid_business_email", type: "string" },
            ],
          },
        },
      ),

      // 3. Company enrichment Clearbit API
      n("3", "hitApi", "Clearbit: Enrich attendee company data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={attendee_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_attendee_company",
              type: "string",
            },
            {
              name: "industry",
              customName: "attendee_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "attendee_company_size",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "attendee_company_revenue",
              type: "string",
            },
            {
              name: "description",
              customName: "attendee_company_desc",
              type: "string",
            },
          ],
        },
      }),

      // 4. ICP prioritization runCode
      n("4", "runCode", "Prioritize attendee against ICP criteria", 760, {
        runCodeDetails: {
          code: `let score = 0;
const size = parseInt(variables.attendee_company_size || '0');
if (size >= 50 && size <= 5000) score += 25;
else if (size > 5000) score += 15;
const targetIndustries = ['technology','software','marketing','ecommerce','fintech','saas'];
if (targetIndustries.some(i => (variables.attendee_industry || '').toLowerCase().includes(i))) score += 20;
const seniorTitles = ['vp','director','head','chief','cto','cmo','ceo','svp','founder'];
if (seniorTitles.some(t => (variables.attendee_title || '').toLowerCase().includes(t))) score += 20;
if (variables.booth_visited === 'yes') score += 15;
if (variables.is_valid_business_email === 'yes') score += 10;
if (variables.notes && variables.notes.length > 10) score += 10;
output.priority_score = Math.min(100, score);
output.priority_tier = score >= 70 ? 'tier_1' : score >= 40 ? 'tier_2' : 'tier_3';
output.is_high_priority = score >= 60 ? 'yes' : 'no';
output.followup_urgency = score >= 70 ? 'within_24h' : score >= 40 ? 'within_48h' : 'within_week';`,
          outputVariables: [
            { name: "priority_score", type: "number" },
            { name: "priority_tier", type: "string" },
            { name: "is_high_priority", type: "string" },
            { name: "followup_urgency", type: "string" },
          ],
        },
      }),

      // 5. Validation waterfall API
      n("5", "hitApi", "NeverBounce: Validate attendee email address", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={attendee_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "result",
              customName: "attendee_email_valid",
              type: "string",
            },
          ],
        },
      }),

      // 6. Tier routing condition
      n("6", "condition", "Route based on attendee priority tier", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_priority", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. AI personalized follow-up
      aiNode(
        "7",
        "AI draft personalized conference follow-up email",
        1480,
        "You are a conference follow-up specialist. Write a warm, personalized follow-up email referencing the specific event, any booth visit or conversation notes, and the attendee's company context. For high-priority leads, be more direct with a meeting ask. For others, offer valuable content. Return JSON: {subject_line, email_body, personalization_hook, next_step}",
        "Attendee: {attendee_first} {attendee_last}, {attendee_title} at {enriched_attendee_company} | Industry: {attendee_industry} | Size: {attendee_company_size} | Event: {event_name} ({event_date}) | Booth visited: {booth_visited} | Notes: {notes} | Priority: {priority_tier} ({priority_score}/100) | Urgency: {followup_urgency}",
        [
          { field: "subject_line", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "next_step", as: "followup_next_step", type: "string" },
        ],
      ),

      // 8. CRM upsert + event tag HubSpot
      hubspotPostNode(
        "8",
        "Upsert HubSpot contact with event tags and follow-up data",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{attendee_email}","firstname":"{attendee_first}","lastname":"{attendee_last}","jobtitle":"{attendee_title}","company":"{enriched_attendee_company}","industry":"{attendee_industry}","hs_lead_status":"NEW","notes_last_updated":"Conference: {event_name} ({event_date}). Priority: {priority_tier} ({priority_score}/100). Booth: {booth_visited}. Notes: {notes}. Personalization: {personalization_hook}."}',
          },
        ],
        [
          { name: "id", customName: "attendee_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "upserted_attendee_email",
            type: "string",
          },
        ],
      ),

      // 9. Send + Slack queue
      slackNode(
        "9",
        "Queue follow-up send and notify sales team",
        1960,
        "📋 *Conference Follow-Up Queued*\\n*Event:* {event_name} ({event_date})\\n*Attendee:* {attendee_first} {attendee_last}, {attendee_title} at {enriched_attendee_company}\\n*Priority:* {priority_tier} ({priority_score}/100) | Urgency: {followup_urgency}\\n*Industry:* {attendee_industry} | Size: {attendee_company_size}\\n*Booth Visited:* {booth_visited}\\n*Notes:* {notes}\\n*Personalization:* {personalization_hook}\\n*Next Step:* {followup_next_step}\\n*CRM ID:* {attendee_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1758. CONFERENCE SPEAKER PITCH (7 nodes)
  // ===================================================================

  "Conference Speaker Pitch": {
    summary:
      "Runs on a schedule to research upcoming conferences, analyzes audience and topic fit with AI, generates a speaker proposal with AI, discovers conference organizer contacts via API, validates emails, drafts a pitch email with AI, and logs everything to Google Sheets for tracking.",
    nodes: [
      trigger(
        "Schedule: Weekly conference speaker opportunity scan",
        "schedule",
        {
          schedule: "Every Monday at 10:00 AM",
          howItStarts:
            "Fires weekly to identify upcoming conferences relevant to our expertise, generate speaker proposals, and pitch conference organizers to secure speaking slots",
          setup:
            "Configure OpenAI key for AI research and generation. Set up Hunter.io API for organizer contact discovery. Provide NeverBounce key for validation. Configure Google Sheets API for tracking. Define expertise topics and target conference themes.",
          inputFields: [
            {
              name: "conference_name",
              type: "string",
              example: "SaaStr Annual 2025",
            },
            {
              name: "conference_url",
              type: "string",
              example: "https://saastrannual.com",
            },
            {
              name: "our_expertise",
              type: "string",
              example: "B2B marketing automation and AI-driven outreach",
            },
            { name: "speaker_name", type: "string", example: "Jane Smith" },
            {
              name: "speaker_bio",
              type: "string",
              example: "VP Marketing at OmniEngage, 15 years in B2B SaaS",
            },
          ],
        },
      ),

      // 2. AI conference research
      aiNode(
        "2",
        "AI research conference themes and audience profile",
        280,
        "You are a conference research analyst. Given a conference name and URL, analyze the likely themes, target audience, typical attendee profile, and speaking opportunity requirements. Return JSON: {conference_themes, target_audience, attendee_count_estimate, speaker_format, submission_deadline_estimate, key_tracks, audience_pain_points, competitive_speakers}",
        "Conference: {conference_name} | URL: {conference_url} | Our expertise: {our_expertise}",
        [
          {
            field: "conference_themes",
            as: "conference_themes",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "attendee_count_estimate",
            as: "attendee_estimate",
            type: "string",
          },
          { field: "speaker_format", as: "speaker_format", type: "string" },
          { field: "key_tracks", as: "key_tracks", type: "string" },
          {
            field: "audience_pain_points",
            as: "audience_pain_points",
            type: "string",
          },
        ],
      ),

      // 3. AI audience topic fit
      aiNode(
        "3",
        "AI assess topic-audience fit and generate talk proposals",
        520,
        "You are a thought leadership strategist. Given conference themes and our expertise, propose 3 compelling talk titles with abstracts that would resonate with the audience. Each should address specific audience pain points. Return JSON: {talk_1_title, talk_1_abstract, talk_2_title, talk_2_abstract, talk_3_title, talk_3_abstract, recommended_talk, fit_score, positioning_angle}",
        "Conference: {conference_name} | Themes: {conference_themes} | Audience: {target_audience} | Pain points: {audience_pain_points} | Key tracks: {key_tracks} | Our expertise: {our_expertise} | Speaker: {speaker_name} — {speaker_bio}",
        [
          { field: "talk_1_title", as: "talk_1_title", type: "string" },
          { field: "talk_1_abstract", as: "talk_1_abstract", type: "string" },
          { field: "talk_2_title", as: "talk_2_title", type: "string" },
          { field: "talk_2_abstract", as: "talk_2_abstract", type: "string" },
          { field: "recommended_talk", as: "recommended_talk", type: "string" },
          { field: "fit_score", as: "topic_fit_score", type: "number" },
          {
            field: "positioning_angle",
            as: "positioning_angle",
            type: "string",
          },
        ],
      ),

      // 4. AI speaker proposal generation
      aiNode(
        "4",
        "AI generate formal speaker proposal document",
        760,
        "You are a conference speaking proposal writer. Create a concise, compelling speaker proposal that includes the recommended talk, speaker credentials, audience takeaways, and social proof. Return JSON: {proposal_title, proposal_summary, key_takeaways, speaker_credentials, social_proof, talk_duration, audience_level}",
        "Recommended talk: {recommended_talk} | Title: {talk_1_title} | Abstract: {talk_1_abstract} | Alt: {talk_2_title} — {talk_2_abstract} | Speaker: {speaker_name} — {speaker_bio} | Conference: {conference_name} | Audience: {target_audience} | Fit score: {topic_fit_score}/100 | Angle: {positioning_angle}",
        [
          { field: "proposal_title", as: "proposal_title", type: "string" },
          { field: "proposal_summary", as: "proposal_summary", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          {
            field: "speaker_credentials",
            as: "speaker_credentials",
            type: "string",
          },
          { field: "talk_duration", as: "talk_duration", type: "string" },
        ],
      ),

      // 5. Organizer discovery API
      n(
        "5",
        "hitApi",
        "Hunter.io: Discover conference organizer contacts",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/domain-search?domain={conference_url}&api_key={hunter_api_key}&department=executive,marketing&limit=3",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.emails[0].value",
                customName: "organizer_email",
                type: "string",
              },
              {
                name: "data.emails[0].first_name",
                customName: "organizer_first_name",
                type: "string",
              },
              {
                name: "data.emails[0].last_name",
                customName: "organizer_last_name",
                type: "string",
              },
              {
                name: "data.emails[0].position",
                customName: "organizer_title",
                type: "string",
              },
            ],
          },
        },
      ),

      // 6. Email validation API
      n("6", "hitApi", "NeverBounce: Validate organizer email", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.neverbounce.com/v4/single/check?key={neverbounce_key}&email={organizer_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "result",
              customName: "organizer_email_valid",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI pitch email draft + Sheets track
      sheetsNode(
        "7",
        "Log speaker pitch to tracking spreadsheet",
        1480,
        "Speaker Pitches",
        '[["{conference_name}","{organizer_email}","{organizer_first_name} {organizer_last_name}","{proposal_title}","{topic_fit_score}","{talk_duration}","pitched","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1759. CONTACT-TO-ACCOUNT MATCHING (8 nodes)
  // ===================================================================

  "Contact-to-Account Matching": {
    summary:
      "Runs on a schedule to find orphaned CRM contacts without associated accounts. Extracts email domains with runCode, looks up existing CRM accounts by domain in HubSpot, runs fuzzy company name matching with runCode, enriches unmatched domains via Clearbit, scores match confidence with runCode, gates on auto-match threshold via condition, and routes high-confidence matches to auto-association or manual review queue in Google Sheets.",
    nodes: [
      trigger("Schedule: Daily orphaned contact matcher", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Fires daily to identify CRM contacts without associated company accounts and automatically match them using domain extraction, fuzzy matching, and enrichment data",
        setup:
          "Configure HubSpot API token with contact and company read/write access. Set up Clearbit API for domain enrichment. Configure Google Sheets API for manual review queue. Define match confidence thresholds for auto vs manual matching.",
        inputFields: [],
      }),

      // 2. Orphaned contact extraction HubSpot
      hubspotGetNode(
        "2",
        "Fetch orphaned contacts without company association",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "orphan_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "orphan_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "orphan_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "orphan_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "orphan_company_text",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "orphan_title",
            type: "string",
          },
          { name: "total", customName: "total_orphans", type: "number" },
        ],
      ),

      // 3. Domain extraction runCode
      n(
        "3",
        "runCode",
        "Extract and normalize email domain for matching",
        520,
        {
          runCodeDetails: {
            code: `const email = (variables.orphan_email || '').toLowerCase();
const domain = email.split('@')[1] || '';
const freeProviders = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','protonmail.com'];
output.extracted_domain = domain;
output.is_business_email = !freeProviders.includes(domain) ? 'yes' : 'no';
output.domain_root = domain.replace(/^www\\./, '').split('.').slice(-2).join('.');
output.company_name_from_domain = domain.replace(/\\.(com|io|co|org|net|ai).*$/, '').replace(/[.-]/g, ' ');
output.company_text_normalized = (variables.orphan_company_text || '').toLowerCase().replace(/[^a-z0-9\\s]/g, '').trim();`,
            outputVariables: [
              { name: "extracted_domain", type: "string" },
              { name: "is_business_email", type: "string" },
              { name: "domain_root", type: "string" },
              { name: "company_name_from_domain", type: "string" },
              { name: "company_text_normalized", type: "string" },
            ],
          },
        },
      ),

      // 4. CRM domain lookup HubSpot
      hubspotGetNode(
        "4",
        "Search HubSpot companies by domain",
        760,
        "crm/v3/objects/companies/search",
        [
          {
            name: "results[0].id",
            customName: "matched_company_id",
            type: "string",
          },
          {
            name: "results[0].properties.name",
            customName: "matched_company_name",
            type: "string",
          },
          {
            name: "results[0].properties.domain",
            customName: "matched_company_domain",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "matched_company_industry",
            type: "string",
          },
          { name: "total", customName: "domain_match_count", type: "number" },
        ],
      ),

      // 5. Fuzzy company match runCode
      n("5", "runCode", "Run fuzzy company name matching algorithm", 1000, {
        runCodeDetails: {
          code: `const contactCompany = variables.company_text_normalized || '';
const crmCompany = (variables.matched_company_name || '').toLowerCase().replace(/[^a-z0-9\\s]/g, '').trim();
const domainCompany = variables.company_name_from_domain || '';
// Simple Levenshtein-like similarity
function similarity(a, b) {
  if (!a || !b) return 0;
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  if (longer.length === 0) return 1;
  const includes = longer.includes(shorter) || shorter.includes(longer);
  if (includes) return 0.9;
  let matches = 0;
  const words_a = a.split(' ');
  const words_b = b.split(' ');
  for (const w of words_a) { if (words_b.includes(w)) matches++; }
  return matches / Math.max(words_a.length, words_b.length);
}
const domainMatchScore = variables.domain_match_count > 0 ? 90 : 0;
const nameMatchScore = Math.round(similarity(contactCompany, crmCompany) * 100);
const domainNameScore = Math.round(similarity(contactCompany, domainCompany) * 100);
output.domain_match_score = domainMatchScore;
output.name_match_score = nameMatchScore;
output.best_match_score = Math.max(domainMatchScore, nameMatchScore, domainNameScore);
output.match_method = domainMatchScore >= 90 ? 'domain_exact' : nameMatchScore >= 70 ? 'name_fuzzy' : 'enrichment_needed';`,
          outputVariables: [
            { name: "domain_match_score", type: "number" },
            { name: "name_match_score", type: "number" },
            { name: "best_match_score", type: "number" },
            { name: "match_method", type: "string" },
          ],
        },
      }),

      // 6. Clearbit enrichment API
      n(
        "6",
        "hitApi",
        "Clearbit: Enrich unmatched domain for company data",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={extracted_domain}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "name",
                customName: "clearbit_company_name",
                type: "string",
              },
              { name: "domain", customName: "clearbit_domain", type: "string" },
              {
                name: "industry",
                customName: "clearbit_industry",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "clearbit_employees",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. Match confidence scoring runCode
      n(
        "7",
        "runCode",
        "Calculate final match confidence and route decision",
        1480,
        {
          runCodeDetails: {
            code: `let confidence = parseInt(variables.best_match_score || '0');
if (variables.clearbit_company_name && variables.matched_company_name) {
  const clearbitNorm = variables.clearbit_company_name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const crmNorm = variables.matched_company_name.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (clearbitNorm === crmNorm || clearbitNorm.includes(crmNorm) || crmNorm.includes(clearbitNorm)) {
    confidence = Math.max(confidence, 95);
  }
}
output.final_confidence = confidence;
output.auto_match = confidence >= 80 ? 'yes' : 'no';
output.route_action = confidence >= 80 ? 'auto_associate' : confidence >= 50 ? 'manual_review' : 'create_new';
output.match_summary = 'Contact: ' + variables.orphan_email + ' | Company: ' + (variables.matched_company_name || variables.clearbit_company_name || 'Unknown') + ' | Confidence: ' + confidence + '% | Action: ' + output.route_action;`,
            outputVariables: [
              { name: "final_confidence", type: "number" },
              { name: "auto_match", type: "string" },
              { name: "route_action", type: "string" },
              { name: "match_summary", type: "string" },
            ],
          },
        },
      ),

      // 8. Auto-match gate condition + manual review queue Sheets
      n("8", "condition", "Route based on match confidence threshold", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "auto_match", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1760. CONTRACT RENEWAL OUTREACH (8 nodes)
  // ===================================================================

  "Contract Renewal Outreach": {
    summary:
      "Runs on a schedule to monitor upcoming contract renewals. Aggregates usage data with runCode, generates usage highlight copy with AI, summarizes new features relevant to the account with runCode, assesses renewal risk via condition, drafts a personalized renewal email with AI, and sends the email plus updates the deal stage in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly renewal outreach pipeline", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Fires weekly to identify contracts coming up for renewal in the next 60 days, enriching each with usage data and crafting personalized renewal outreach to maximize retention",
        setup:
          "Configure HubSpot API token with deal and contact read/write access. Set up usage analytics API or database query access. Configure OpenAI key for AI generation. Define renewal window (e.g., 60 days out).",
        inputFields: [],
      }),

      // 2. Renewal monitor HubSpot
      hubspotGetNode(
        "2",
        "Fetch deals with upcoming renewal dates from HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          {
            name: "results[0].id",
            customName: "renewal_deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealname",
            customName: "renewal_deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "renewal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "renewal_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_deal_stage_probability",
            customName: "renewal_probability",
            type: "string",
          },
          { name: "total", customName: "total_renewals", type: "number" },
        ],
      ),

      // 3. Usage aggregation runCode
      n(
        "3",
        "runCode",
        "Aggregate customer usage metrics for renewal context",
        520,
        {
          runCodeDetails: {
            code: `// Simulates aggregating usage data from analytics
output.monthly_active_users = Math.floor(Math.random() * 500) + 50;
output.feature_adoption_rate = Math.floor(Math.random() * 40) + 60;
output.support_tickets_last_quarter = Math.floor(Math.random() * 10);
output.nps_score = Math.floor(Math.random() * 40) + 30;
output.usage_trend = output.monthly_active_users > 200 ? 'growing' : output.monthly_active_users > 100 ? 'stable' : 'declining';
output.health_score = Math.round((output.feature_adoption_rate * 0.4) + (output.nps_score * 0.3) + (output.monthly_active_users > 100 ? 30 : 15));
output.renewal_risk = output.health_score < 50 ? 'high' : output.health_score < 70 ? 'medium' : 'low';
output.usage_summary = 'MAU: ' + output.monthly_active_users + ' | Adoption: ' + output.feature_adoption_rate + '% | NPS: ' + output.nps_score + ' | Health: ' + output.health_score + '/100';`,
            outputVariables: [
              { name: "monthly_active_users", type: "number" },
              { name: "feature_adoption_rate", type: "number" },
              { name: "support_tickets_last_quarter", type: "number" },
              { name: "nps_score", type: "number" },
              { name: "usage_trend", type: "string" },
              { name: "health_score", type: "number" },
              { name: "renewal_risk", type: "string" },
              { name: "usage_summary", type: "string" },
            ],
          },
        },
      ),

      // 4. AI usage highlight generation
      aiNode(
        "4",
        "AI generate usage highlights and value narrative",
        760,
        "You are a customer success writer. Given usage metrics for a customer approaching renewal, generate compelling value highlights that demonstrate ROI and usage. Focus on specific metrics that show success. Return JSON: {value_headline, usage_highlights, roi_narrative, underused_features, growth_opportunity}",
        "Customer: {renewal_deal_name} | Contract value: ${renewal_amount} | Renewal date: {renewal_date} | MAU: {monthly_active_users} | Feature adoption: {feature_adoption_rate}% | NPS: {nps_score} | Usage trend: {usage_trend} | Health score: {health_score}/100 | Support tickets: {support_tickets_last_quarter}",
        [
          { field: "value_headline", as: "value_headline", type: "string" },
          { field: "usage_highlights", as: "usage_highlights", type: "string" },
          { field: "roi_narrative", as: "roi_narrative", type: "string" },
          {
            field: "underused_features",
            as: "underused_features",
            type: "string",
          },
          {
            field: "growth_opportunity",
            as: "growth_opportunity",
            type: "string",
          },
        ],
      ),

      // 5. New-feature summary runCode
      n(
        "5",
        "runCode",
        "Compile new features relevant to this customer",
        1000,
        {
          runCodeDetails: {
            code: `// In production, this would query a feature release log
output.new_features = 'AI-powered workflow builder, Advanced analytics dashboard, Multi-channel campaign orchestration, Real-time collaboration tools';
output.feature_count = 4;
output.most_relevant_feature = 'AI-powered workflow builder — automates repetitive campaign setup, saving ~5 hours/week';
output.upgrade_available = parseInt(variables.renewal_amount || '0') < 50000 ? 'yes' : 'no';
output.upgrade_suggestion = output.upgrade_available === 'yes' ? 'Enterprise tier unlocks advanced analytics and AI features' : 'Current tier includes all new features';`,
            outputVariables: [
              { name: "new_features", type: "string" },
              { name: "feature_count", type: "number" },
              { name: "most_relevant_feature", type: "string" },
              { name: "upgrade_available", type: "string" },
              { name: "upgrade_suggestion", type: "string" },
            ],
          },
        },
      ),

      // 6. Renewal risk assessment condition
      n(
        "6",
        "condition",
        "Check renewal risk level for outreach strategy",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "renewal_risk", operator: "equals", value: "high" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. AI renewal draft
      aiNode(
        "7",
        "AI draft personalized renewal outreach email",
        1480,
        "You are a customer retention specialist. Write a renewal outreach email that leads with value, references specific usage highlights, introduces new features, and makes the renewal feel exciting rather than administrative. For high-risk accounts, be more proactive about scheduling a call. For low-risk, focus on expansion. Return JSON: {subject_line, email_body, urgency_level, closing_cta}",
        "Customer: {renewal_deal_name} | Amount: ${renewal_amount} | Renewal date: {renewal_date} | Risk: {renewal_risk} | Value headline: {value_headline} | Usage highlights: {usage_highlights} | ROI: {roi_narrative} | New features ({feature_count}): {new_features} | Most relevant: {most_relevant_feature} | Underused: {underused_features} | Growth opportunity: {growth_opportunity} | Upgrade: {upgrade_suggestion}",
        [
          { field: "subject_line", as: "renewal_subject", type: "string" },
          { field: "email_body", as: "renewal_body", type: "string" },
          { field: "urgency_level", as: "renewal_urgency", type: "string" },
          { field: "closing_cta", as: "renewal_cta", type: "string" },
        ],
      ),

      // 8. Send + stage update email HubSpot
      hubspotPostNode(
        "8",
        "Update HubSpot deal stage and log renewal outreach",
        1720,
        "crm/v3/objects/deals/{renewal_deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Renewal outreach sent. Risk: {renewal_risk}. Health: {health_score}/100. Usage: {usage_summary}. New features highlighted: {feature_count}. Upgrade available: {upgrade_available}. CTA: {renewal_cta}.","hs_deal_stage_probability":"{renewal_probability}"}',
          },
        ],
        [
          { name: "id", customName: "updated_renewal_deal_id", type: "string" },
          {
            name: "properties.notes_last_updated",
            customName: "renewal_note_logged",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1761. CREATOR SPONSORSHIP OUTREACH (8 nodes)
  // ===================================================================

  "Creator Sponsorship Outreach": {
    summary:
      "Runs on a schedule to discover content creators for sponsorship partnerships. Enriches creator profiles with AI, scores creator-brand fit with runCode, gates on fit threshold via condition, resolves contact information via API, generates a personalized sponsorship pitch with AI, and sends the email plus creates a CRM entry in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly creator sponsorship discovery", "schedule", {
        schedule: "Every Tuesday at 9:00 AM",
        howItStarts:
          "Fires weekly to discover and evaluate content creators for sponsorship opportunities, scoring brand fit and generating personalized partnership pitches",
        setup:
          "Configure Social Blade or similar API for creator discovery. Set up OpenAI key for AI enrichment and pitch generation. Provide Hunter.io API key for contact resolution. Ensure HubSpot token has deal creation access.",
        inputFields: [
          { name: "creator_handle", type: "string", example: "@techreviewer" },
          { name: "creator_platform", type: "string", example: "youtube" },
          { name: "our_brand", type: "string", example: "OmniEngage" },
          { name: "campaign_budget", type: "string", example: "5000" },
        ],
      }),

      // 2. Creator discovery API
      n("2", "hitApi", "Social Blade: Fetch creator profile and metrics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://matrix.sbapis.com/b/{creator_platform}/statistics?query={creator_handle}&clientid={socialblade_client_id}&token={socialblade_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.statistics.subscribers.count",
              customName: "subscriber_count",
              type: "number",
            },
            {
              name: "data.statistics.views.average",
              customName: "avg_views",
              type: "number",
            },
            {
              name: "data.statistics.engagement_rate",
              customName: "engagement_rate",
              type: "string",
            },
            {
              name: "data.statistics.uploads.count",
              customName: "total_uploads",
              type: "number",
            },
            {
              name: "data.statistics.grade",
              customName: "creator_grade",
              type: "string",
            },
            { name: "data.title", customName: "creator_name", type: "string" },
          ],
        },
      }),

      // 3. AI creator enrichment
      aiNode(
        "3",
        "AI analyze creator content and audience alignment",
        520,
        "You are a creator marketing analyst. Given a creator's metrics and platform, analyze their content niche, audience demographics (estimated), brand safety, and sponsorship history. Return JSON: {content_niche, estimated_audience_demo, brand_safety_score, sponsorship_readiness, content_quality, audience_overlap_estimate, cpm_estimate, recommended_format}",
        "Creator: {creator_name} ({creator_handle}) on {creator_platform} | Subscribers: {subscriber_count} | Avg views: {avg_views} | Engagement: {engagement_rate} | Uploads: {total_uploads} | Grade: {creator_grade} | Our brand: {our_brand} | Budget: ${campaign_budget}",
        [
          { field: "content_niche", as: "content_niche", type: "string" },
          {
            field: "estimated_audience_demo",
            as: "audience_demo",
            type: "string",
          },
          {
            field: "brand_safety_score",
            as: "brand_safety_score",
            type: "number",
          },
          {
            field: "sponsorship_readiness",
            as: "sponsorship_readiness",
            type: "string",
          },
          {
            field: "audience_overlap_estimate",
            as: "audience_overlap",
            type: "string",
          },
          { field: "cpm_estimate", as: "cpm_estimate", type: "string" },
          {
            field: "recommended_format",
            as: "recommended_format",
            type: "string",
          },
        ],
      ),

      // 4. Fit scoring runCode
      n("4", "runCode", "Calculate creator-brand fit score", 760, {
        runCodeDetails: {
          code: `let score = 0;
const subs = parseInt(variables.subscriber_count || '0');
if (subs >= 10000 && subs <= 500000) score += 25; // sweet spot for authenticity + reach
else if (subs > 500000) score += 20;
else if (subs >= 1000) score += 15;
const engagement = parseFloat(variables.engagement_rate || '0');
if (engagement > 5) score += 25;
else if (engagement > 2) score += 20;
else score += 10;
const safety = parseInt(variables.brand_safety_score || '0');
score += Math.round(safety * 0.2);
const overlap = (variables.audience_overlap || '').toLowerCase();
if (overlap.includes('high') || overlap.includes('strong')) score += 15;
else if (overlap.includes('medium') || overlap.includes('moderate')) score += 10;
output.fit_score = Math.min(100, score);
output.fit_tier = score >= 70 ? 'excellent' : score >= 50 ? 'good' : 'fair';
output.passes_threshold = score >= 50 ? 'yes' : 'no';
output.estimated_roi = subs > 0 ? Math.round((parseInt(variables.avg_views || '0') / parseInt(variables.campaign_budget || '1')) * 10) : 0;`,
          outputVariables: [
            { name: "fit_score", type: "number" },
            { name: "fit_tier", type: "string" },
            { name: "passes_threshold", type: "string" },
            { name: "estimated_roi", type: "number" },
          ],
        },
      }),

      // 5. Threshold gate condition
      n(
        "5",
        "condition",
        "Check if creator meets sponsorship fit threshold",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "passes_threshold",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Contact resolution API
      n(
        "6",
        "hitApi",
        "Hunter.io: Resolve creator business contact email",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hunter.io/v2/email-finder?domain={creator_handle}.com&api_key={hunter_api_key}&first_name={creator_name}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "data.email",
                customName: "creator_email",
                type: "string",
              },
              {
                name: "data.confidence",
                customName: "email_confidence",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. AI sponsorship pitch generation
      aiNode(
        "7",
        "AI generate personalized sponsorship pitch email",
        1480,
        "You are a creator partnership outreach specialist. Write a genuine, non-generic sponsorship pitch that references the creator's specific content, explains why our brand aligns with their audience, and proposes a concrete collaboration format and budget. Be respectful of their creative autonomy. Return JSON: {subject_line, email_body, collaboration_hook, proposed_deal}",
        "Creator: {creator_name} ({creator_handle}) on {creator_platform} | Niche: {content_niche} | Subscribers: {subscriber_count} | Engagement: {engagement_rate} | Audience: {audience_demo} | Overlap: {audience_overlap} | Format: {recommended_format} | Our brand: {our_brand} | Budget: ${campaign_budget} | Fit: {fit_tier} ({fit_score}/100) | CPM: {cpm_estimate}",
        [
          { field: "subject_line", as: "sponsor_subject", type: "string" },
          { field: "email_body", as: "sponsor_body", type: "string" },
          {
            field: "collaboration_hook",
            as: "collaboration_hook",
            type: "string",
          },
          { field: "proposed_deal", as: "proposed_deal", type: "string" },
        ],
      ),

      // 8. Send + CRM entry email HubSpot
      hubspotPostNode(
        "8",
        "Create HubSpot deal for creator sponsorship",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Creator Sponsorship — {creator_name} ({creator_platform})","dealstage":"appointmentscheduled","pipeline":"default","amount":"{campaign_budget}","description":"Creator: {creator_name} ({creator_handle}). Platform: {creator_platform}. Subscribers: {subscriber_count}. Engagement: {engagement_rate}. Fit: {fit_score}/100 ({fit_tier}). Niche: {content_niche}. Format: {recommended_format}. ROI est: {estimated_roi}. Contact: {creator_email}."}',
          },
        ],
        [
          { name: "id", customName: "sponsorship_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "sponsorship_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1762. CUSTOM DEMO BRIEF GENERATOR (7 nodes)
  // ===================================================================

  "Custom Demo Brief Generator": {
    summary:
      "Receives a webhook when a demo is scheduled, aggregates prospect data from HubSpot, uses AI to research the company and industry, maps pain points to use cases with AI, generates a demo script with AI, anticipates objections with AI, and assembles the complete brief for delivery via email and Slack.",
    nodes: [
      trigger("Webhook: Demo meeting scheduled", "webhook_app", {
        source: "calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when a prospect schedules a demo via Calendly or similar scheduling tool, triggering automated brief generation that prepares the sales rep with company research, pain points, demo script, and objection handling",
        setup:
          "Configure Calendly webhook for invitee.created events. Ensure HubSpot API token has contact and deal read access. Configure OpenAI key for AI research and generation. Set up Slack webhook for demo prep channel. Configure email for brief delivery.",
        inputFields: [],
      }),

      // 2. Prospect data aggregation HubSpot
      n(
        "2",
        "runCode",
        "Extract prospect data from demo scheduling webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload.payload || payload;
const invitee = data.invitee || data;
output.prospect_email = invitee.email || invitee.invitee_email || '';
output.prospect_name = invitee.name || invitee.invitee_name || '';
output.prospect_company = invitee.questions_and_answers?.find(q => q.question?.toLowerCase().includes('company'))?.answer || '';
output.prospect_role = invitee.questions_and_answers?.find(q => q.question?.toLowerCase().includes('role') || q.question?.toLowerCase().includes('title'))?.answer || '';
output.demo_date = data.event?.start_time || data.scheduled_at || '';
output.demo_type = data.event?.name || data.event_type || 'Product Demo';
output.prospect_domain = output.prospect_email.split('@')[1] || '';
output.sales_rep_email = data.event?.event_memberships?.[0]?.user_email || '';`,
            outputVariables: [
              { name: "prospect_email", type: "string" },
              { name: "prospect_name", type: "string" },
              { name: "prospect_company", type: "string" },
              { name: "prospect_role", type: "string" },
              { name: "demo_date", type: "string" },
              { name: "demo_type", type: "string" },
              { name: "prospect_domain", type: "string" },
              { name: "sales_rep_email", type: "string" },
            ],
          },
        },
      ),

      // 3. AI company industry research
      aiNode(
        "3",
        "AI research prospect company and industry context",
        520,
        "You are a pre-sales research analyst. Research the prospect's company based on their email domain and company name. Identify their industry, typical challenges, competitive landscape, and recent news or trends affecting them. Return JSON: {company_overview, industry_vertical, typical_challenges, competitive_landscape, recent_trends, company_size_estimate, tech_sophistication}",
        "Company: {prospect_company} | Domain: {prospect_domain} | Contact: {prospect_name}, {prospect_role}",
        [
          { field: "company_overview", as: "company_overview", type: "string" },
          {
            field: "industry_vertical",
            as: "industry_vertical",
            type: "string",
          },
          {
            field: "typical_challenges",
            as: "typical_challenges",
            type: "string",
          },
          {
            field: "competitive_landscape",
            as: "competitive_landscape",
            type: "string",
          },
          { field: "recent_trends", as: "recent_trends", type: "string" },
          {
            field: "company_size_estimate",
            as: "company_size_estimate",
            type: "string",
          },
          {
            field: "tech_sophistication",
            as: "tech_sophistication",
            type: "string",
          },
        ],
      ),

      // 4. AI pain use-case mapping
      aiNode(
        "4",
        "AI map prospect pain points to product use cases",
        760,
        "You are a solutions architect. Given the prospect's industry, role, and challenges, map their likely pain points to specific product use cases and features. Prioritize the 3 most impactful use cases. Return JSON: {pain_point_1, use_case_1, pain_point_2, use_case_2, pain_point_3, use_case_3, discovery_questions, value_proposition}",
        "Company: {prospect_company} ({industry_vertical}) | Role: {prospect_role} | Challenges: {typical_challenges} | Tech level: {tech_sophistication} | Trends: {recent_trends} | Size: {company_size_estimate}",
        [
          { field: "pain_point_1", as: "pain_point_1", type: "string" },
          { field: "use_case_1", as: "use_case_1", type: "string" },
          { field: "pain_point_2", as: "pain_point_2", type: "string" },
          { field: "use_case_2", as: "use_case_2", type: "string" },
          { field: "pain_point_3", as: "pain_point_3", type: "string" },
          { field: "use_case_3", as: "use_case_3", type: "string" },
          {
            field: "discovery_questions",
            as: "discovery_questions",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 5. AI demo script generation
      aiNode(
        "5",
        "AI generate customized demo script and flow",
        1000,
        "You are a demo engineer. Create a structured demo script that leads with the prospect's pain points, demonstrates relevant use cases, and builds toward a strong closing. Include timing estimates for each section. Return JSON: {demo_opening, section_1, section_2, section_3, wow_moment, closing_script, total_duration, transition_tips}",
        "Prospect: {prospect_name}, {prospect_role} at {prospect_company} | Demo type: {demo_type} | Pain 1: {pain_point_1} -> {use_case_1} | Pain 2: {pain_point_2} -> {use_case_2} | Pain 3: {pain_point_3} -> {use_case_3} | Discovery questions: {discovery_questions} | Value prop: {value_proposition}",
        [
          { field: "demo_opening", as: "demo_opening", type: "string" },
          { field: "section_1", as: "demo_section_1", type: "string" },
          { field: "section_2", as: "demo_section_2", type: "string" },
          { field: "section_3", as: "demo_section_3", type: "string" },
          { field: "wow_moment", as: "demo_wow_moment", type: "string" },
          { field: "closing_script", as: "demo_closing", type: "string" },
          { field: "total_duration", as: "demo_duration", type: "string" },
        ],
      ),

      // 6. AI objection anticipation
      aiNode(
        "6",
        "AI anticipate likely objections and prepare responses",
        1240,
        "You are a sales objection handler. Based on the prospect's profile, anticipate the 3 most likely objections and prepare responses. Return JSON: {objection_1, response_1, objection_2, response_2, objection_3, response_3, pricing_approach, competitor_mention_strategy}",
        "Prospect: {prospect_name}, {prospect_role} at {prospect_company} ({industry_vertical}) | Size: {company_size_estimate} | Competitive landscape: {competitive_landscape} | Tech sophistication: {tech_sophistication} | Key value: {value_proposition}",
        [
          { field: "objection_1", as: "objection_1", type: "string" },
          { field: "response_1", as: "response_1", type: "string" },
          { field: "objection_2", as: "objection_2", type: "string" },
          { field: "response_2", as: "response_2", type: "string" },
          { field: "objection_3", as: "objection_3", type: "string" },
          { field: "response_3", as: "response_3", type: "string" },
          { field: "pricing_approach", as: "pricing_approach", type: "string" },
        ],
      ),

      // 7. Brief assembly + delivery email Slack
      slackNode(
        "7",
        "Deliver complete demo brief to sales rep via Slack",
        1480,
        "📋 *Demo Brief: {prospect_name} at {prospect_company}*\\n*Date:* {demo_date} | *Type:* {demo_type}\\n*Role:* {prospect_role} | *Industry:* {industry_vertical}\\n\\n🏢 *Company:* {company_overview}\\n\\n🎯 *Pain Points & Use Cases:*\\n1. {pain_point_1} → {use_case_1}\\n2. {pain_point_2} → {use_case_2}\\n3. {pain_point_3} → {use_case_3}\\n\\n❓ *Discovery Questions:* {discovery_questions}\\n\\n📝 *Demo Script ({demo_duration}):*\\n*Opening:* {demo_opening}\\n*Section 1:* {demo_section_1}\\n*Section 2:* {demo_section_2}\\n*Section 3:* {demo_section_3}\\n*Wow Moment:* {demo_wow_moment}\\n*Close:* {demo_closing}\\n\\n🛡️ *Objection Prep:*\\n1. {objection_1} → {response_1}\\n2. {objection_2} → {response_2}\\n3. {objection_3} → {response_3}\\n\\n💰 *Pricing:* {pricing_approach}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1763. CUSTOM VIDEO BRIEF FOR OUTREACH (7 nodes)
  // ===================================================================

  "Custom Video Brief for Outreach": {
    summary:
      "Receives a webhook when a sales rep requests a personalized video for a prospect, aggregates prospect research with AI, analyzes the prospect context with AI, generates a video script with AI, creates talking-point guides with runCode, checks video platform readiness with runCode, and delivers the brief via Slack plus logs the activity in HubSpot.",
    nodes: [
      trigger("Webhook: Personalized video outreach requested", "webhook_app", {
        source: "internal",
        event: "video_request.created",
        howItStarts:
          "Fires when a sales rep requests a personalized video for a prospect, triggering AI-powered script generation with prospect-specific talking points and delivery instructions",
        setup:
          "Configure webhook endpoint for video requests. Set up OpenAI key for AI research and script generation. Configure Slack webhook for video production channel. Ensure HubSpot token has activity logging access.",
        inputFields: [],
      }),

      // 2. AI prospect research aggregation
      n("2", "runCode", "Extract video request context from webhook", 280, {
        runCodeDetails: {
          code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.prospect_email = data.prospect_email || data.email || '';
output.prospect_name = data.prospect_name || data.name || '';
output.prospect_company = data.company || data.prospect_company || '';
output.prospect_title = data.title || data.prospect_title || '';
output.prospect_linkedin = data.linkedin_url || data.linkedin || '';
output.deal_context = data.deal_context || data.notes || '';
output.video_purpose = data.purpose || data.video_type || 'introductory outreach';
output.rep_name = data.rep_name || data.requested_by || '';
output.rep_email = data.rep_email || '';
output.prospect_domain = output.prospect_email.split('@')[1] || '';`,
          outputVariables: [
            { name: "prospect_email", type: "string" },
            { name: "prospect_name", type: "string" },
            { name: "prospect_company", type: "string" },
            { name: "prospect_title", type: "string" },
            { name: "prospect_linkedin", type: "string" },
            { name: "deal_context", type: "string" },
            { name: "video_purpose", type: "string" },
            { name: "rep_name", type: "string" },
            { name: "rep_email", type: "string" },
            { name: "prospect_domain", type: "string" },
          ],
        },
      }),

      // 3. AI context analysis
      aiNode(
        "3",
        "AI research prospect for video personalization context",
        520,
        "You are a prospect research specialist preparing context for a personalized video. Research the prospect and their company to find specific details that can be referenced in the video to demonstrate genuine interest. Return JSON: {company_summary, recent_achievement, industry_trend, shared_connection, personalization_detail, prospect_priority, recommended_tone}",
        "Prospect: {prospect_name}, {prospect_title} at {prospect_company} | Domain: {prospect_domain} | LinkedIn: {prospect_linkedin} | Deal context: {deal_context} | Video purpose: {video_purpose}",
        [
          {
            field: "company_summary",
            as: "video_company_summary",
            type: "string",
          },
          {
            field: "recent_achievement",
            as: "recent_achievement",
            type: "string",
          },
          {
            field: "industry_trend",
            as: "video_industry_trend",
            type: "string",
          },
          {
            field: "personalization_detail",
            as: "personalization_detail",
            type: "string",
          },
          {
            field: "prospect_priority",
            as: "prospect_priority",
            type: "string",
          },
          { field: "recommended_tone", as: "recommended_tone", type: "string" },
        ],
      ),

      // 4. AI video script generation
      aiNode(
        "4",
        "AI generate personalized video script",
        760,
        "You are a video script writer for sales outreach. Write a 60-90 second video script that opens with a personalized hook, delivers value, and ends with a clear call-to-action. The script should feel natural and conversational, not read from a teleprompter. Return JSON: {hook_line, body_script, cta_line, suggested_visual_cues, estimated_duration, energy_level}",
        "Prospect: {prospect_name}, {prospect_title} at {prospect_company} | Purpose: {video_purpose} | Company: {video_company_summary} | Recent: {recent_achievement} | Trend: {video_industry_trend} | Personalization: {personalization_detail} | Tone: {recommended_tone} | Rep: {rep_name} | Deal context: {deal_context}",
        [
          { field: "hook_line", as: "video_hook", type: "string" },
          { field: "body_script", as: "video_body", type: "string" },
          { field: "cta_line", as: "video_cta", type: "string" },
          { field: "suggested_visual_cues", as: "visual_cues", type: "string" },
          { field: "estimated_duration", as: "video_duration", type: "string" },
          { field: "energy_level", as: "energy_level", type: "string" },
        ],
      ),

      // 5. Talking-points guide runCode
      n(
        "5",
        "runCode",
        "Compile talking-point guide and recording instructions",
        1000,
        {
          runCodeDetails: {
            code: `output.recording_checklist = '1. Quiet environment with good lighting\\n2. Look at the camera, not the screen\\n3. Use prospect name within first 5 seconds\\n4. Keep it under ' + variables.video_duration + '\\n5. Smile and be genuine\\n6. Record 2 takes minimum';
output.key_talking_points = '- Hook: ' + variables.video_hook + '\\n- Personalization: ' + variables.personalization_detail + '\\n- Value: Reference ' + variables.video_industry_trend + '\\n- CTA: ' + variables.video_cta;
output.do_not_mention = 'Avoid: pricing specifics, competitor names, assumptions about their current tools';
output.thumbnail_suggestion = 'Show prospect company name on a whiteboard or screen share behind you';
output.brief_id = 'VB-' + Date.now().toString(36).toUpperCase();`,
            outputVariables: [
              { name: "recording_checklist", type: "string" },
              { name: "key_talking_points", type: "string" },
              { name: "do_not_mention", type: "string" },
              { name: "thumbnail_suggestion", type: "string" },
              { name: "brief_id", type: "string" },
            ],
          },
        },
      ),

      // 6. Video-platform check runCode
      n(
        "6",
        "runCode",
        "Check video platform settings and prepare upload details",
        1240,
        {
          runCodeDetails: {
            code: `output.platform = 'vidyard'; // or loom, etc.
output.upload_instructions = 'Record via Vidyard browser extension or desktop app. Tag with prospect email: ' + variables.prospect_email;
output.tracking_enabled = 'yes';
output.notification_on_view = 'Notify ' + variables.rep_name + ' (' + variables.rep_email + ') when ' + variables.prospect_name + ' views the video';
output.follow_up_trigger = 'If viewed > 80%, auto-trigger follow-up email sequence';
output.delivery_method = 'Embed in personalized email with GIF thumbnail preview';`,
            outputVariables: [
              { name: "platform", type: "string" },
              { name: "upload_instructions", type: "string" },
              { name: "tracking_enabled", type: "string" },
              { name: "notification_on_view", type: "string" },
              { name: "follow_up_trigger", type: "string" },
              { name: "delivery_method", type: "string" },
            ],
          },
        },
      ),

      // 7. Brief delivery + CRM activity Slack HubSpot
      slackNode(
        "7",
        "Deliver video brief to sales rep via Slack",
        1480,
        "🎬 *Video Brief Ready: {brief_id}*\\n*For:* {prospect_name}, {prospect_title} at {prospect_company}\\n*Purpose:* {video_purpose} | *Priority:* {prospect_priority}\\n*Duration:* {video_duration} | *Tone:* {recommended_tone} | *Energy:* {energy_level}\\n\\n📝 *Script:*\\n*Hook:* {video_hook}\\n*Body:* {video_body}\\n*CTA:* {video_cta}\\n\\n🎯 *Talking Points:*\\n{key_talking_points}\\n\\n⚠️ *Avoid:* {do_not_mention}\\n\\n🖼️ *Thumbnail:* {thumbnail_suggestion}\\n*Visual Cues:* {visual_cues}\\n\\n✅ *Recording Checklist:*\\n{recording_checklist}\\n\\n📤 *Upload:* {upload_instructions}\\n*Platform:* {platform} | *Delivery:* {delivery_method}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1764. CUSTOMER ADVOCATE RECRUITING (8 nodes)
  // ===================================================================

  "Customer Advocate Recruiting": {
    summary:
      "Runs on a schedule to identify happy customers for advocacy programs. Scores advocacy readiness with runCode, matches to available programs with runCode, gates on score threshold via condition, generates personalized advocacy asks with AI, queues for CSM review via approval gate, and sends the advocacy ask email plus tracks in Google Sheets.",
    nodes: [
      trigger("Schedule: Monthly advocate candidate scan", "schedule", {
        schedule: "First Monday of every month at 10:00 AM",
        howItStarts:
          "Fires monthly to identify happy, engaged customers who are strong candidates for advocacy programs such as case studies, reviews, referrals, or speaking engagements",
        setup:
          "Configure HubSpot API token with contact and deal read access. Set up OpenAI key for personalized advocacy ask generation. Configure Google Sheets API for pipeline tracking. Define advocacy program criteria and available programs.",
        inputFields: [],
      }),

      // 2. Happy-customer identification HubSpot
      hubspotGetNode(
        "2",
        "Fetch high-NPS engaged customers from HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "advocate_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "advocate_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "advocate_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "advocate_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "advocate_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "advocate_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "advocate_status",
            type: "string",
          },
          { name: "total", customName: "total_candidates", type: "number" },
        ],
      ),

      // 3. Advocacy readiness scoring runCode
      n("3", "runCode", "Score customer advocacy readiness", 520, {
        runCodeDetails: {
          code: `let score = 0;
// Customer tenure — longer is better
const status = variables.advocate_status || '';
if (status === 'CUSTOMER') score += 20;
// Engagement signals (simulated — in production these come from usage analytics)
const hasRenewed = true; // Would check renewal history
if (hasRenewed) score += 20;
// Title seniority — senior advocates have more credibility
const title = (variables.advocate_title || '').toLowerCase();
const senior = ['vp','director','head','chief','svp','founder','ceo','cmo','cto'];
if (senior.some(t => title.includes(t))) score += 20;
// Company brand recognition
const company = variables.advocate_company || '';
if (company.length > 3) score += 10;
// Base score for being in candidate pool
score += 15;
output.advocacy_score = Math.min(100, score);
output.advocacy_tier = score >= 70 ? 'champion' : score >= 50 ? 'supporter' : 'passive';
output.passes_threshold = score >= 50 ? 'yes' : 'no';
output.best_program_fit = score >= 70 ? 'case_study_and_speaking' : score >= 50 ? 'review_and_referral' : 'review_only';`,
          outputVariables: [
            { name: "advocacy_score", type: "number" },
            { name: "advocacy_tier", type: "string" },
            { name: "passes_threshold", type: "string" },
            { name: "best_program_fit", type: "string" },
          ],
        },
      }),

      // 4. Program fit matching runCode
      n("4", "runCode", "Match customer to specific advocacy programs", 760, {
        runCodeDetails: {
          code: `const fit = variables.best_program_fit || 'review_only';
const programs = [];
if (fit.includes('case_study') || fit.includes('speaking')) {
  programs.push('Case Study Interview (30 min, featured on website)');
  programs.push('Conference Co-Presentation Opportunity');
  programs.push('Video Testimonial (15 min recording)');
}
if (fit.includes('referral') || fit.includes('review')) {
  programs.push('G2/Capterra Review (5 min, $25 gift card)');
  programs.push('Customer Referral Program ($500 credit per referral)');
}
programs.push('Beta Feature Preview Access');
output.matched_programs = programs.join(' | ');
output.primary_ask = programs[0] || 'G2 Review';
output.secondary_ask = programs[1] || '';
output.incentive = fit.includes('case_study') ? 'Premium: co-marketing, conference sponsorship, and early feature access' : 'Standard: gift card and referral credits';
output.estimated_commitment = fit.includes('case_study') ? '2-3 hours total' : '15-30 minutes';`,
          outputVariables: [
            { name: "matched_programs", type: "string" },
            { name: "primary_ask", type: "string" },
            { name: "secondary_ask", type: "string" },
            { name: "incentive", type: "string" },
            { name: "estimated_commitment", type: "string" },
          ],
        },
      }),

      // 5. Threshold gate condition
      n(
        "5",
        "condition",
        "Check if customer meets advocacy readiness threshold",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "passes_threshold",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. AI personalized ask generation
      aiNode(
        "6",
        "AI generate personalized advocacy ask email",
        1240,
        "You are a customer advocacy program manager. Write a warm, appreciative email asking a happy customer to participate in an advocacy program. Lead with genuine gratitude, explain the specific program and incentives, and make it easy to say yes. Return JSON: {subject_line, email_body, appreciation_hook, easy_yes_cta}",
        "Customer: {advocate_first} {advocate_last}, {advocate_title} at {advocate_company} | Tier: {advocacy_tier} ({advocacy_score}/100) | Primary ask: {primary_ask} | Secondary: {secondary_ask} | Incentive: {incentive} | Commitment: {estimated_commitment} | All programs: {matched_programs}",
        [
          { field: "subject_line", as: "advocacy_subject", type: "string" },
          { field: "email_body", as: "advocacy_body", type: "string" },
          {
            field: "appreciation_hook",
            as: "appreciation_hook",
            type: "string",
          },
          { field: "easy_yes_cta", as: "easy_yes_cta", type: "string" },
        ],
      ),

      // 7. CSM review queue approval gate
      n("7", "approvalGate", "CSM reviews advocacy ask before sending", 1480, {
        approvalGateDetails: {
          approvers: ["{csm_email}"],
          message:
            "Advocacy ask for {advocate_first} {advocate_last} ({advocate_company}).\n\nTier: {advocacy_tier} ({advocacy_score}/100)\nPrimary Ask: {primary_ask}\nIncentive: {incentive}\nCommitment: {estimated_commitment}\n\nSubject: {advocacy_subject}\n\nApprove to send the advocacy ask email.",
          timeoutHours: 48,
        },
      }),

      // 8. Advocacy ask send + pipeline tracking email Sheets
      sheetsNode(
        "8",
        "Log advocacy pipeline to tracking spreadsheet",
        1720,
        "Advocacy Pipeline",
        '[["{advocate_first} {advocate_last}","{advocate_company}","{advocate_email}","{advocacy_tier}","{advocacy_score}","{primary_ask}","{incentive}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1765. CUSTOMER DATA PROOF POINT (8 nodes)
  // ===================================================================

  "Customer Data Proof Point": {
    summary:
      "Receives a webhook with a prospect profile, queries HubSpot for similar customer cohort data, aggregates success metrics with runCode, validates statistical significance with runCode, generates a proof-point narrative with AI, checks anonymization requirements via condition, formats the proof-point one-pager with runCode, and delivers via email plus attaches to the CRM deal in HubSpot.",
    nodes: [
      trigger("Webhook: Proof point requested for prospect", "webhook_app", {
        source: "internal",
        event: "proof_point.requested",
        howItStarts:
          "Fires when a sales rep requests customer data proof points for a specific prospect, triggering automated cohort analysis and proof-point generation based on similar customer success stories",
        setup:
          "Configure webhook endpoint for proof point requests. Ensure HubSpot API token has deal and contact read access. Configure OpenAI key for narrative generation. Define data anonymization rules and thresholds.",
        inputFields: [],
      }),

      // 2. Similar-cohort query HubSpot
      n(
        "2",
        "runCode",
        "Extract prospect profile from webhook for cohort matching",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.prospect_company = data.company || data.prospect_company || '';
output.prospect_industry = data.industry || data.prospect_industry || '';
output.prospect_size = data.company_size || data.employees || '';
output.prospect_use_case = data.use_case || data.interest || '';
output.deal_id = data.deal_id || '';
output.rep_email = data.rep_email || data.requested_by || '';
output.prospect_email = data.prospect_email || '';
output.prospect_name = data.prospect_name || '';`,
            outputVariables: [
              { name: "prospect_company", type: "string" },
              { name: "prospect_industry", type: "string" },
              { name: "prospect_size", type: "string" },
              { name: "prospect_use_case", type: "string" },
              { name: "deal_id", type: "string" },
              { name: "rep_email", type: "string" },
              { name: "prospect_email", type: "string" },
              { name: "prospect_name", type: "string" },
            ],
          },
        },
      ),

      // 3. Similar-cohort query HubSpot
      hubspotGetNode(
        "3",
        "Query HubSpot for similar customer cohort data",
        520,
        "crm/v3/objects/deals/search",
        [
          {
            name: "results[0].properties.dealname",
            customName: "cohort_customer_1",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "cohort_deal_1_amount",
            type: "string",
          },
          {
            name: "results[1].properties.dealname",
            customName: "cohort_customer_2",
            type: "string",
          },
          {
            name: "results[1].properties.amount",
            customName: "cohort_deal_2_amount",
            type: "string",
          },
          {
            name: "results[2].properties.dealname",
            customName: "cohort_customer_3",
            type: "string",
          },
          { name: "total", customName: "cohort_size", type: "number" },
        ],
      ),

      // 4. Success-metrics aggregation runCode
      n("4", "runCode", "Aggregate success metrics from customer cohort", 760, {
        runCodeDetails: {
          code: `const cohortSize = parseInt(variables.cohort_size || '0');
// In production, these would come from actual customer analytics
output.avg_time_to_value = '14 days';
output.avg_roi_percentage = '340%';
output.avg_retention_rate = '96%';
output.avg_nps_score = '72';
output.top_metric_improvement = '47% increase in conversion rates';
output.median_deal_size = Math.round(((parseFloat(variables.cohort_deal_1_amount || '0') + parseFloat(variables.cohort_deal_2_amount || '0')) / 2)).toString();
output.cohort_description = cohortSize + ' similar companies in ' + variables.prospect_industry + ' with ' + variables.prospect_size + ' employees';
output.statistical_confidence = cohortSize >= 30 ? 'high' : cohortSize >= 10 ? 'medium' : 'low';`,
          outputVariables: [
            { name: "avg_time_to_value", type: "string" },
            { name: "avg_roi_percentage", type: "string" },
            { name: "avg_retention_rate", type: "string" },
            { name: "avg_nps_score", type: "string" },
            { name: "top_metric_improvement", type: "string" },
            { name: "median_deal_size", type: "string" },
            { name: "cohort_description", type: "string" },
            { name: "statistical_confidence", type: "string" },
          ],
        },
      }),

      // 5. Statistical validation runCode
      n(
        "5",
        "runCode",
        "Validate statistical significance of proof points",
        1000,
        {
          runCodeDetails: {
            code: `const confidence = variables.statistical_confidence || 'low';
const cohortSize = parseInt(variables.cohort_size || '0');
output.is_statistically_significant = cohortSize >= 10 ? 'yes' : 'no';
output.confidence_level = confidence === 'high' ? '95%' : confidence === 'medium' ? '85%' : '70%';
output.sample_size_note = cohortSize >= 30 ? 'Strong sample size (' + cohortSize + ' customers)' : cohortSize >= 10 ? 'Adequate sample size (' + cohortSize + ' customers)' : 'Limited sample — use directional language';
output.recommended_language = cohortSize >= 30 ? 'On average, companies like yours see...' : cohortSize >= 10 ? 'Companies similar to yours typically experience...' : 'Early customers in your space have reported...';
output.disclaimer = cohortSize < 10 ? 'Results may vary. Based on a limited customer cohort.' : 'Based on aggregated, anonymized customer data.';`,
            outputVariables: [
              { name: "is_statistically_significant", type: "string" },
              { name: "confidence_level", type: "string" },
              { name: "sample_size_note", type: "string" },
              { name: "recommended_language", type: "string" },
              { name: "disclaimer", type: "string" },
            ],
          },
        },
      ),

      // 6. AI proof-point narrative
      aiNode(
        "6",
        "AI generate compelling proof-point narrative",
        1240,
        "You are a customer success storyteller. Given cohort metrics and prospect context, craft a compelling proof-point narrative that demonstrates value for a similar company. Use the recommended language style based on statistical confidence. Return JSON: {headline, narrative_body, key_stat_callouts, prospect_specific_angle, testimonial_snippet}",
        "Prospect: {prospect_name} at {prospect_company} ({prospect_industry}, {prospect_size} employees) | Use case: {prospect_use_case} | Cohort: {cohort_description} | ROI: {avg_roi_percentage} | Time to value: {avg_time_to_value} | Retention: {avg_retention_rate} | NPS: {avg_nps_score} | Top improvement: {top_metric_improvement} | Language: {recommended_language} | Confidence: {confidence_level} | Sample note: {sample_size_note}",
        [
          { field: "headline", as: "proof_headline", type: "string" },
          { field: "narrative_body", as: "proof_narrative", type: "string" },
          { field: "key_stat_callouts", as: "proof_stats", type: "string" },
          {
            field: "prospect_specific_angle",
            as: "proof_angle",
            type: "string",
          },
          {
            field: "testimonial_snippet",
            as: "proof_testimonial",
            type: "string",
          },
        ],
      ),

      // 7. Anonymization check condition
      n(
        "7",
        "condition",
        "Check if proof points meet anonymization requirements",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "is_statistically_significant",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. AI proof-point one-pager runCode + Delivery email HubSpot
      emailNode(
        "8",
        "Send proof-point one-pager to sales rep",
        1720,
        "{rep_email}",
        "Proof Point Brief: {prospect_company} — {proof_headline}",
        "Hi,\\n\\nHere is the proof-point brief for {prospect_name} at {prospect_company}.\\n\\n{proof_headline}\\n\\n{proof_narrative}\\n\\nKey Stats:\\n{proof_stats}\\n\\nProspect-Specific Angle:\\n{proof_angle}\\n\\nTestimonial Snippet:\\n{proof_testimonial}\\n\\nCohort Details:\\n{cohort_description}\\nConfidence: {confidence_level} | {sample_size_note}\\n\\n{disclaimer}\\n\\nUse this in your next conversation with {prospect_name}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1766. CUSTOMER HEALTH EXPANSION SIGNAL (9 nodes)
  // ===================================================================

  "Customer Health Expansion Signal": {
    summary:
      "Runs on a schedule to ingest customer health scores from HubSpot, detects usage growth patterns with runCode, classifies expansion opportunities with AI, estimates incremental revenue with runCode, gates on revenue threshold via condition, maps stakeholders with runCode, personalizes the expansion pitch with AI, and creates an expansion deal plus dispatches outreach via email and Slack in HubSpot.",
    nodes: [
      trigger(
        "Schedule: Daily health-based expansion signal scan",
        "schedule",
        {
          schedule: "Every day at 7:00 AM",
          howItStarts:
            "Fires daily to analyze customer health scores and usage patterns, identifying expansion-ready accounts based on growth signals, high engagement, and feature adoption that indicate readiness for upsell or cross-sell",
          setup:
            "Configure HubSpot API token with contact, deal, and company read/write access. Set up OpenAI key for AI classification and pitch generation. Configure Slack webhook for expansion alerts channel. Define health score and revenue thresholds for expansion qualification.",
          inputFields: [],
        },
      ),

      // 2. Health-score ingestion HubSpot
      hubspotGetNode(
        "2",
        "Fetch high-health customers with expansion potential",
        280,
        "crm/v3/objects/companies/search",
        [
          { name: "results[0].id", customName: "company_id", type: "string" },
          {
            name: "results[0].properties.name",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "customer_industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "customer_employees",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "customer_arr",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "customer_status",
            type: "string",
          },
          {
            name: "total",
            customName: "total_healthy_accounts",
            type: "number",
          },
        ],
      ),

      // 3. Usage-growth detection runCode
      n(
        "3",
        "runCode",
        "Detect usage growth patterns and expansion signals",
        520,
        {
          runCodeDetails: {
            code: `// In production, this queries usage analytics APIs
const employees = parseInt(variables.customer_employees || '0');
const arr = parseFloat(variables.customer_arr || '0');
output.current_seats = Math.floor(employees * 0.3);
output.active_seats = Math.floor(output.current_seats * 0.85);
output.seat_utilization = output.current_seats > 0 ? Math.round((output.active_seats / output.current_seats) * 100) : 0;
output.usage_growth_rate = 15; // percent MoM
output.feature_adoption_rate = 72;
output.api_call_growth = 25;
output.is_growing = output.usage_growth_rate > 10 || output.seat_utilization > 80 ? 'yes' : 'no';
output.growth_signals = [];
if (output.seat_utilization > 80) output.growth_signals.push('High seat utilization (' + output.seat_utilization + '%)');
if (output.usage_growth_rate > 10) output.growth_signals.push('Usage growing ' + output.usage_growth_rate + '% MoM');
if (output.feature_adoption_rate > 70) output.growth_signals.push('Strong feature adoption (' + output.feature_adoption_rate + '%)');
output.growth_signal_summary = output.growth_signals.join(', ') || 'Stable usage';`,
            outputVariables: [
              { name: "current_seats", type: "number" },
              { name: "active_seats", type: "number" },
              { name: "seat_utilization", type: "number" },
              { name: "usage_growth_rate", type: "number" },
              { name: "feature_adoption_rate", type: "number" },
              { name: "api_call_growth", type: "number" },
              { name: "is_growing", type: "string" },
              { name: "growth_signal_summary", type: "string" },
            ],
          },
        },
      ),

      // 4. AI expansion classification
      aiNode(
        "4",
        "AI classify expansion opportunity type and timing",
        760,
        "You are a customer expansion analyst. Given usage data and growth signals, classify the expansion opportunity. Determine if this is a seat expansion, tier upgrade, cross-sell, or add-on opportunity. Return JSON: {expansion_type, expansion_readiness, recommended_product, expansion_narrative, timing_recommendation, risk_factors}",
        "Customer: {customer_name} ({customer_industry}) | Employees: {customer_employees} | ARR: ${customer_arr} | Seats: {active_seats}/{current_seats} ({seat_utilization}%) | Usage growth: {usage_growth_rate}% MoM | Feature adoption: {feature_adoption_rate}% | API growth: {api_call_growth}% | Signals: {growth_signal_summary}",
        [
          { field: "expansion_type", as: "expansion_type", type: "string" },
          {
            field: "expansion_readiness",
            as: "expansion_readiness",
            type: "string",
          },
          {
            field: "recommended_product",
            as: "recommended_product",
            type: "string",
          },
          {
            field: "expansion_narrative",
            as: "expansion_narrative",
            type: "string",
          },
          {
            field: "timing_recommendation",
            as: "timing_recommendation",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
        ],
      ),

      // 5. Revenue estimation runCode
      n("5", "runCode", "Estimate incremental expansion revenue", 1000, {
        runCodeDetails: {
          code: `const currentArr = parseFloat(variables.customer_arr || '0');
const type = (variables.expansion_type || '').toLowerCase();
let multiplier = 0;
if (type.includes('seat')) multiplier = 0.3;
else if (type.includes('tier') || type.includes('upgrade')) multiplier = 0.5;
else if (type.includes('cross-sell')) multiplier = 0.4;
else multiplier = 0.2;
output.estimated_expansion_arr = Math.round(currentArr * multiplier);
output.estimated_deal_value = output.estimated_expansion_arr;
output.passes_revenue_threshold = output.estimated_expansion_arr >= 5000 ? 'yes' : 'no';
output.revenue_tier = output.estimated_expansion_arr >= 50000 ? 'enterprise' : output.estimated_expansion_arr >= 15000 ? 'mid-market' : 'growth';
output.expansion_summary = variables.expansion_type + ' — est. $' + output.estimated_expansion_arr + ' ARR increase for ' + variables.customer_name + '. Readiness: ' + variables.expansion_readiness + '. Timing: ' + variables.timing_recommendation;`,
          outputVariables: [
            { name: "estimated_expansion_arr", type: "number" },
            { name: "estimated_deal_value", type: "number" },
            { name: "passes_revenue_threshold", type: "string" },
            { name: "revenue_tier", type: "string" },
            { name: "expansion_summary", type: "string" },
          ],
        },
      }),

      // 6. Threshold gate condition
      n(
        "6",
        "condition",
        "Check if expansion meets minimum revenue threshold",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "passes_revenue_threshold",
                operator: "equals",
                value: "yes",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Stakeholder mapping runCode
      n(
        "7",
        "runCode",
        "Map key stakeholders for expansion conversation",
        1480,
        {
          runCodeDetails: {
            code: `output.primary_contact = 'Account owner / CSM contact from CRM';
output.executive_sponsor = 'VP or Director level — needed for ' + variables.revenue_tier + ' expansion';
output.champion = 'Power user with highest activity — likely advocate for expansion';
output.blocker_risk = variables.risk_factors || 'Budget approval cycle';
output.meeting_format = parseInt(variables.estimated_expansion_arr || '0') > 25000 ? 'QBR-style with executive sponsor' : 'Casual usage review with champion';
output.prep_materials = 'Usage report, ROI calculator, expansion pricing proposal';`,
            outputVariables: [
              { name: "primary_contact", type: "string" },
              { name: "executive_sponsor", type: "string" },
              { name: "champion", type: "string" },
              { name: "blocker_risk", type: "string" },
              { name: "meeting_format", type: "string" },
              { name: "prep_materials", type: "string" },
            ],
          },
        },
      ),

      // 8. AI pitch personalization
      aiNode(
        "8",
        "AI personalize expansion pitch for the account",
        1720,
        "You are a customer success expansion specialist. Write a pitch that frames the expansion as a natural next step based on their growth. Reference specific usage data and success metrics. Return JSON: {pitch_subject, pitch_body, value_hook, growth_celebration, expansion_cta}",
        "Customer: {customer_name} ({customer_industry}) | Type: {expansion_type} | Est. value: ${estimated_expansion_arr} | Tier: {revenue_tier} | Current ARR: ${customer_arr} | Seats: {active_seats}/{current_seats} ({seat_utilization}%) | Growth: {usage_growth_rate}% MoM | Narrative: {expansion_narrative} | Product: {recommended_product} | Timing: {timing_recommendation} | Meeting: {meeting_format}",
        [
          { field: "pitch_subject", as: "expansion_subject", type: "string" },
          { field: "pitch_body", as: "expansion_body", type: "string" },
          { field: "value_hook", as: "expansion_hook", type: "string" },
          {
            field: "growth_celebration",
            as: "growth_celebration",
            type: "string",
          },
          { field: "expansion_cta", as: "expansion_cta", type: "string" },
        ],
      ),

      // 9. CRM expansion creation + dispatch email Slack HubSpot
      hubspotPostNode(
        "9",
        "Create HubSpot expansion deal and alert team",
        1960,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Expansion — {customer_name} ({expansion_type})","dealstage":"qualifiedtobuy","pipeline":"default","amount":"{estimated_expansion_arr}","description":"Expansion type: {expansion_type}. Readiness: {expansion_readiness}. Growth signals: {growth_signal_summary}. Recommended product: {recommended_product}. Timing: {timing_recommendation}. Meeting format: {meeting_format}. Risk: {blocker_risk}. Hook: {expansion_hook}."}',
          },
        ],
        [
          { name: "id", customName: "expansion_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "expansion_deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1767. CUSTOMER REFERENCE MATCH (8 nodes)
  // ===================================================================

  "Customer Reference Match": {
    summary:
      "Receives a webhook with deal context for a reference request, queries the HubSpot reference pool for suitable candidates, runs multi-dimensional AI matching, checks reference fatigue with runCode, ranks top matches with runCode, drafts introduction emails with AI, routes through CSM approval via approval gate, and logs the reference activity in Google Sheets and HubSpot.",
    nodes: [
      trigger(
        "Webhook: Customer reference requested for active deal",
        "webhook_app",
        {
          source: "internal",
          event: "reference.requested",
          howItStarts:
            "Fires when a sales rep requests a customer reference for an active deal, triggering automated matching against the reference pool based on industry, size, use case, and reference availability",
          setup:
            "Configure webhook endpoint for reference requests. Ensure HubSpot API token has contact and deal read access. Set up OpenAI key for AI matching and draft generation. Configure Google Sheets API for reference tracking. Set up approval workflow for CSM sign-off.",
          inputFields: [],
        },
      ),

      // 2. Reference-pool query HubSpot
      n(
        "2",
        "runCode",
        "Extract deal context and reference requirements from webhook",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.deal_id = data.deal_id || '';
output.deal_name = data.deal_name || '';
output.prospect_company = data.prospect_company || '';
output.prospect_industry = data.prospect_industry || '';
output.prospect_size = data.prospect_size || '';
output.prospect_use_case = data.use_case || '';
output.prospect_objections = data.objections || '';
output.rep_email = data.rep_email || '';
output.rep_name = data.rep_name || '';
output.urgency = data.urgency || 'normal';
output.preferred_format = data.format || 'call'; // call, email, video`,
            outputVariables: [
              { name: "deal_id", type: "string" },
              { name: "deal_name", type: "string" },
              { name: "prospect_company", type: "string" },
              { name: "prospect_industry", type: "string" },
              { name: "prospect_size", type: "string" },
              { name: "prospect_use_case", type: "string" },
              { name: "prospect_objections", type: "string" },
              { name: "rep_email", type: "string" },
              { name: "rep_name", type: "string" },
              { name: "urgency", type: "string" },
              { name: "preferred_format", type: "string" },
            ],
          },
        },
      ),

      // 3. Reference pool query HubSpot
      hubspotGetNode(
        "3",
        "Query HubSpot reference pool for matching candidates",
        520,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "ref_1_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "ref_1_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "ref_1_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "ref_1_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "ref_1_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "ref_1_title",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "ref_1_industry",
            type: "string",
          },
          {
            name: "results[1].properties.email",
            customName: "ref_2_email",
            type: "string",
          },
          {
            name: "results[1].properties.firstname",
            customName: "ref_2_first",
            type: "string",
          },
          {
            name: "results[1].properties.company",
            customName: "ref_2_company",
            type: "string",
          },
          { name: "total", customName: "pool_size", type: "number" },
        ],
      ),

      // 4. AI multi-dimensional matching
      aiNode(
        "4",
        "AI match references across industry, size, use case, and persona",
        760,
        "You are a customer reference matching specialist. Given a prospect's profile and a pool of reference candidates, score and rank the matches based on industry alignment, company size similarity, use case relevance, and persona match. Return JSON: {best_match_name, best_match_company, match_score, match_reasoning, industry_alignment, size_similarity, use_case_relevance, second_match_name, second_match_company, second_match_score}",
        "Prospect: {prospect_company} | Industry: {prospect_industry} | Size: {prospect_size} | Use case: {prospect_use_case} | Objections: {prospect_objections} | Ref 1: {ref_1_first} {ref_1_last} at {ref_1_company} ({ref_1_industry}), {ref_1_title} | Ref 2: {ref_2_first} at {ref_2_company} | Pool size: {pool_size}",
        [
          { field: "best_match_name", as: "best_ref_name", type: "string" },
          {
            field: "best_match_company",
            as: "best_ref_company",
            type: "string",
          },
          { field: "match_score", as: "best_match_score", type: "number" },
          { field: "match_reasoning", as: "match_reasoning", type: "string" },
          {
            field: "industry_alignment",
            as: "industry_alignment",
            type: "string",
          },
          {
            field: "use_case_relevance",
            as: "use_case_relevance",
            type: "string",
          },
          { field: "second_match_name", as: "second_ref_name", type: "string" },
          {
            field: "second_match_company",
            as: "second_ref_company",
            type: "string",
          },
        ],
      ),

      // 5. Reference fatigue check runCode
      n("5", "runCode", "Check reference fatigue and availability", 1000, {
        runCodeDetails: {
          code: `// In production, this would check a reference activity log
const lastRefDate = new Date(Date.now() - 45 * 24 * 60 * 60 * 1000); // Simulated last reference: 45 days ago
const daysSinceLastRef = Math.floor((Date.now() - lastRefDate.getTime()) / (1000 * 60 * 60 * 24));
output.days_since_last_reference = daysSinceLastRef;
output.reference_fatigue_risk = daysSinceLastRef < 30 ? 'high' : daysSinceLastRef < 60 ? 'medium' : 'low';
output.is_available = daysSinceLastRef >= 30 ? 'yes' : 'no';
output.references_this_quarter = daysSinceLastRef < 90 ? 2 : 0;
output.fatigue_note = daysSinceLastRef < 30 ? 'Reference was used ' + daysSinceLastRef + ' days ago — consider using backup' : 'Reference available — last used ' + daysSinceLastRef + ' days ago';
output.recommended_ref = output.is_available === 'yes' ? variables.best_ref_name : variables.second_ref_name;
output.recommended_company = output.is_available === 'yes' ? variables.best_ref_company : variables.second_ref_company;`,
          outputVariables: [
            { name: "days_since_last_reference", type: "number" },
            { name: "reference_fatigue_risk", type: "string" },
            { name: "is_available", type: "string" },
            { name: "references_this_quarter", type: "number" },
            { name: "fatigue_note", type: "string" },
            { name: "recommended_ref", type: "string" },
            { name: "recommended_company", type: "string" },
          ],
        },
      }),

      // 6. Top-match ranking runCode
      n(
        "6",
        "runCode",
        "Compile final reference recommendation and prep materials",
        1240,
        {
          runCodeDetails: {
            code: `output.final_recommendation = variables.recommended_ref + ' at ' + variables.recommended_company;
output.match_quality = parseInt(variables.best_match_score || '0') >= 80 ? 'excellent' : parseInt(variables.best_match_score || '0') >= 60 ? 'good' : 'fair';
output.talking_points_for_ref = 'Prospect: ' + variables.prospect_company + ' (' + variables.prospect_industry + '). They are interested in: ' + variables.prospect_use_case + '. Key objections to address: ' + variables.prospect_objections;
output.intro_format = variables.preferred_format || 'call';
output.reference_brief = 'Match score: ' + variables.best_match_score + '/100. Reasoning: ' + variables.match_reasoning + '. Fatigue: ' + variables.reference_fatigue_risk + '. Format: ' + output.intro_format;`,
            outputVariables: [
              { name: "final_recommendation", type: "string" },
              { name: "match_quality", type: "string" },
              { name: "talking_points_for_ref", type: "string" },
              { name: "intro_format", type: "string" },
              { name: "reference_brief", type: "string" },
            ],
          },
        },
      ),

      // 7. AI introduction drafts + CSM approval approval gate
      n(
        "7",
        "approvalGate",
        "CSM approves reference match before introduction",
        1480,
        {
          approvalGateDetails: {
            approvers: ["{csm_email}"],
            message:
              "Reference request for deal: {deal_name}\n\nProspect: {prospect_company} ({prospect_industry}, {prospect_size})\nUse case: {prospect_use_case}\nObjections: {prospect_objections}\n\nRecommended Reference: {final_recommendation}\nMatch Quality: {match_quality} ({best_match_score}/100)\nReasoning: {match_reasoning}\nFatigue Risk: {reference_fatigue_risk} ({fatigue_note})\nFormat: {intro_format}\n\nApprove to proceed with reference introduction.",
            timeoutHours: 24,
          },
        },
      ),

      // 8. CRM reference log Sheets HubSpot
      sheetsNode(
        "8",
        "Log reference match to tracking spreadsheet",
        1720,
        "Reference Activity",
        '[["{deal_name}","{prospect_company}","{prospect_industry}","{final_recommendation}","{best_match_score}","{match_quality}","{reference_fatigue_risk}","{intro_format}","approved","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
